import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, forwardRef, lazy, Suspense } from "react";
import { Calendar, CalendarDays, Shovel, Package, Palette, Leaf, MessageCircle, Printer, Scissors, Sprout, Layers, GitFork, Droplet, Droplets, Filter, Flag, Flower2, FileText, Menu, X, ChevronDown, ChevronLeft, ChevronUp, ChevronRight, Pencil, Trash2, Plus, Copy, Settings, Info, Moon, Sun, SunMoon, Download, ArrowLeft, ArrowRight, Search, CheckSquare, FolderOpen, Check, AlertTriangle, ShoppingCart, Bot, CalendarRange, Wrench, Bug, ArrowUpDown, ArrowLeftRight, MoveVertical, MoveHorizontal, TreePine, Lightbulb, TrendingUp, Snowflake, Apple, TreeDeciduous, Sparkles, NotebookPen, CalendarCheck, LayoutGrid, MessageSquare, Send, Shrub, Trees, LeafyGreen, Barcode, Eye, EyeOff, List, ExternalLink, Heart, ShoppingBag, Share2, Trophy, MapPin, MonitorSmartphone, Star, Paperclip, Shield, LogIn, LogOut, UserCircle2 } from "lucide-react";

import { MONTHS, MONTH_FULL, TASK_TYPES, CURRENT_MONTH, CURRENT_YEAR } from "./constants.js";
import { TASK_ICONS, COLOUR_TYPES, buildThemeVars } from "./theme.js";
import { INITIAL_PLANTS } from "./data/plants.js";
import { INITIAL_AREAS } from "./data/areas.js";
import { PRODUCT_RULES, classifyProductFull, classifyProduct, proactivePestMatches } from "./data/products.js";
import { PLANT_SUGGESTIONS } from "./data/suggestions.js";
import { WIKI_ALIASES } from './data/wiki-aliases';
import { SEASONAL_TIPS } from "./data/tips.js";
import { detectRegion, resolveLangToRegion, AFFILIATE_REGIONS, AFFILIATE_PRODUCTS, AFFILIATE_DISCLOSURE, buildAffiliateLink, logAffiliateClick, findMatchingProducts, buildGenericSearchUrl, buildManualSearchUrl, rowHasAffiliateLinks, FEATURE_SOURCES, PLANT_SUPPLIERS, matchProductsInText, matchShoppingProductsInText, inventoryFilter } from "./data/affiliates.js";
import { logEvent, flushEvents, getEvents, clearEvents } from "./data/events.js";
import { FEATURED_SUPPLIERS } from "./data/suppliers.js";
import { useRegisterSW } from "virtual:pwa-register/react";
import { STATIC_PLANT_COLOURS } from "./data/colours.js";
import STATIC_SCHEDULES from './data/schedules.json';
import { matchesTroubleKeyword } from './data/trouble-keywords.js';
import {
  storageKey, dismissKey, monthOffset, safeSetItem,
  persistOnboardingProgress, markOnboardingComplete, resolveOnboardingFirstLoadAction,
  persistOnboardField, persistOnboardArray, persistOnboardJSON,
  migrateGardenType, wipeAllTendData, countAllTasks,
  pushWalOp,
} from './lib/storage.js';
import {
  isDarkColour, fmtSize, darkenHex, djb2,
  _hashStr, _dayOfYear, _slugifyInspire,
} from './lib/format.js';
import {
  PLANT_SUGGESTIONS_BY_LATIN, enrichGrowingConditions, nextPlantId,
  isValidPlant, normalisePendingPlant, normalisePlant,
} from './lib/plants.js';
import {
  UK_POSTCODE_RE, UK_POSTCODE_OUTWARD_RE,
  isValidUkPostcode, normaliseUkPostcode, outwardCode,
  requestGeolocation, reverseGeocode,
} from './lib/location.js';
import {
  TROUBLE_NOTE_COOLDOWN_DAYS, _warnedManualKeys,
  getProductDisplayFor, addManualProductEntry,
  isTroubleShownRecently, recordTroubleShown,
} from './lib/products.js';
import { getPotteringTabLabel } from './lib/labels.js';
import {
  parseSchedule, parseColour, callAI, richDefaultTasks, defaultTasks,
  lookupStaticSchedule, parseStatusResponse, reprobeAiStatus,
  fireAskBeacon, fireAffBeacon, buildSystemPrompt,
  extractPlantAdd, extractPlantAddBatch,
  PARSE_MONTH_ABBREVS, PARSE_MONTH_PATTERN, PARSE_TASK_KEYWORDS,
  TONE_HINTS,
} from './lib/ai.js';
import { getApiBase } from './lib/sync.js';
import { getPublicFlags } from './lib/featureFlags.js';
import {
  REPORTED_IMAGES_WINDOW_MS, REVIEW_DESTINATIONS, UPGRADE_TRIGGERS,
  computeEffectiveFeatures,
  defaultReviewPromptState, defaultUpgradePromptState,
  getReviewDestination, isEngagementEligible,
  isImageReportSuppressed, readReportedImages,
  readReviewPromptState, readUpgradePromptState,
  shouldShowReviewPrompt, shouldShowUpgrade,
} from './lib/engagement.js';
import {
  IMGURL_STORE, IMGURL_CACHE_MAX, _storeImgUrl,
  fetchWikiImage, isPlantPage,
  setImageOverrides, getImageOverrides,
} from './lib/wiki-image.js';
import { findMatchingSuppliers } from './lib/suppliers.js';
import { resolveColour } from './lib/colours.js';
import { getSeasonIcon } from './lib/season-icon.js';
import { isAdminEmail } from './admin/AdminAuth.jsx';
// Phase 38 D-15 — React.lazy per-route code splitting. Each view becomes a
// separate Vite chunk loaded on first navigation. Sub-imports within each
// view folder (e.g. PlantCard inside PlantsView) bundle into the same chunk.
const AdminPanel    = lazy(() => import('./admin/AdminPanel.jsx'));
const PrintView     = lazy(() => import('./views/print/PrintView.jsx'));
const DivisionGuide = lazy(() => import('./views/division/DivisionGuide.jsx'));
const ColourPalette = lazy(() => import('./views/palette/ColourPalette.jsx'));
const ShedInventory = lazy(() => import('./views/supplies/ShedInventory.jsx'));
const GardenSession = lazy(() => import('./views/activity/GardenSession.jsx'));
const AskTend       = lazy(() => import('./views/ask-tend/AskTend.jsx'));
const YearView      = lazy(() => import('./views/year/YearView.jsx'));
const MonthView     = lazy(() => import('./views/month/MonthView.jsx'));
const PlantsView    = lazy(() => import('./views/plants/PlantsView.jsx'));

// These MUST stay as static (synchronous) imports — used outside the view router:
import buildShoppingStack from './views/ask-tend/shopping.js';
import renderMarkdown from './views/ask-tend/markdown.jsx';
import PlantCard from './views/plants/PlantCard.jsx';
import PlantHistory from './views/plants/PlantHistory.jsx';
import PlantNotesLog from './views/plants/PlantNotesLog.jsx';
import PlantDirectoryCard from './views/plants/PlantDirectoryCard.jsx';
import BulkImportPanel from './views/plants/BulkImportPanel.jsx';
import OnboardingNudgeCard from './home-panel/OnboardingNudgeCard.jsx';
import SeasonalTipCard from './home-panel/SeasonalTipCard.jsx';
import ReviewPromptCard from './home-panel/ReviewPromptCard.jsx';
import InventoryAlertCard from './home-panel/InventoryAlertCard.jsx';
import PlantGapCard from './home-panel/PlantGapCard.jsx';
import TroubleProductCard from './home-panel/TroubleProductCard.jsx';
import FeaturedSupplierCard from './home-panel/FeaturedSupplierCard.jsx';
import GardenSummaryCard from './home-panel/GardenSummaryCard.jsx';
import InspireCard, { rankInspireCandidates } from './home-panel/InspireCard.jsx';
import UpgradePrompt, { UpgradePromptHost } from './home-panel/UpgradePrompt.jsx';
import HomePanel from './home-panel/HomePanel.jsx';
import AuthCallback from './auth/AuthCallback.jsx';
import SignInModal from './auth/SignInModal.jsx';
import FirstSignInUploadModal from './auth/FirstSignInUploadModal.jsx';
import SyncedToast from './auth/SyncedToast.jsx';
import UndoToast from './ui/UndoToast.jsx';
import AuthErrorBanner from './auth/AuthErrorBanner.jsx';
import { useSession } from './auth/useSession.js';
import AddPlantSheet from './sheets/AddPlantSheet.jsx';
import AffiliateSheet from './sheets/AffiliateSheet.jsx';
import SupplierSheet from './sheets/SupplierSheet.jsx';
import AddPlantForm from './sheets/AddPlantForm.jsx';
import { LegalModal, PRIVACY_CONTENT, TERMS_CONTENT } from './modals/LegalModal.jsx';
import { ConfirmModal } from './modals/ConfirmModal.jsx';
import AskTendUpgrade from './modals/AskTendUpgrade.jsx';
import EventsViewerModal from './modals/EventsViewerModal.jsx';
import AboutPanel from './modals/AboutPanel.jsx';
import WhyGoPremium from './modals/WhyGoPremium.jsx';
import {
  SHARE_PLATFORM_DIMS,
  layoutAchievementCard,
  renderAchievementCardForPlatform,
  renderAchievementCard,
  layoutRecommendCard,
  renderRecommendCardForPlatform,
} from './modals/share-canvas.js';
import { RecommendModal } from './modals/RecommendModal.jsx';
import { AchievementModal } from './modals/AchievementModal.jsx';
import { ReviewForm } from './modals/ReviewForm.jsx';
import FeedbackModal from './modals/FeedbackModal.jsx';
import AreaManager from './modals/AreaManager.jsx';
import SettingsPanel from './modals/SettingsPanel.jsx';

// Phase 37 Plan 06 — DEV_BLUE shared between SettingsPanel and the
// "Simulating Premium" indicator inside App(). Kept module-private; SettingsPanel
// owns its own copy of the constant so it stays modal-self-contained.
const DEV_BLUE = "#1976d2";

// ─────────────────────────────────────────────────────────────────────────────
// Plan 37-06 transitional re-exports — Plan 37-09 sweeps these from test imports
// then removes the shim. Modals moved to src/modals/.
// ─────────────────────────────────────────────────────────────────────────────
export { LegalModal, PRIVACY_CONTENT, TERMS_CONTENT };
export { ConfirmModal };
export { SHARE_PLATFORM_DIMS, renderAchievementCardForPlatform, renderRecommendCardForPlatform };
export { RecommendModal };
export { AchievementModal };
export { ReviewForm };
import {
  ONBOARD_GARDEN_SPACES, ONBOARD_EXPERIENCE,
  ONB_SKIES, ONB_SKIES_DARK,
  ONB_AREA_PATH_FALLBACK, ONB_AREA_PATHS,
  ONB_GARDEN_SPACE_TO_AREA_LABEL, ONB_SPOTLIGHT_FALLBACKS,
  ONBOARD_PRESET_AREAS,
  GARDEN_SPACE_TO_AREA_LABEL, GARDEN_SPACE_TO_CUSTOM_AREA,
} from './onboarding/constants.js';
import { onbResolveSpotlightColors } from './onboarding/spotlight.js';
import OnbClouds from './onboarding/OnbClouds.jsx';
import OnbGround from './onboarding/OnbGround.jsx';
import OnbBee from './onboarding/OnbBee.jsx';
import OnbButterfly from './onboarding/OnbButterfly.jsx';
import OnbProgressRail from './onboarding/OnbProgressRail.jsx';
import OnbTileArt from './onboarding/OnbTileArt.jsx';
import OnbPlotPlan from './onboarding/OnbPlotPlan.jsx';
import OnbFrame from './onboarding/OnbFrame.jsx';
import OnboardingChip from './onboarding/OnboardingChip.jsx';
import OnboardingWelcome from './onboarding/OnboardingWelcome.jsx';
import OnboardingGardenSpaces from './onboarding/OnboardingGardenSpaces.jsx';
import OnboardingAreas from './onboarding/OnboardingAreas.jsx';
import OnboardingSubSpaces from './onboarding/OnboardingSubSpaces.jsx';
import OnboardingPlant from './onboarding/OnboardingPlant.jsx';
import OnboardingTaskReveal from './onboarding/OnboardingTaskReveal.jsx';
import OnboardingWizard from './onboarding/OnboardingWizard.jsx';
// onboarding.css moved to src/main.jsx (Plan 37-03).

// PLANT_SUGGESTIONS_BY_LATIN + enrichGrowingConditions moved to src/lib/plants.js (Plan 37-02).

const TASK_ICON_COMPONENTS = {
  prune:      Scissors,
  feed:       Sprout,
  mulch:      Layers,
  divide:     GitFork,
  water:      Droplets,
  deadhead:   Flower2,
  "plant/sow":Leaf,
  note:       FileText,
};
// Exported so cross-folder views (PrintView, ShedInventory, GardenSession,
// PlantHistory, etc.) can render task-type glyphs without re-declaring the
// Lucide-icon map. Stays in App.jsx per D-01 (tiny shared atom).
export function TaskIcon({type,size=16,style={}}) {
  const Icon=TASK_ICON_COMPONENTS[type];
  if(!Icon) return null;
  return <Icon size={size} style={{flexShrink:0,...style}}/>;
}

// nextPlantId moved to src/lib/plants.js (Plan 37-02).

// _warnedManualKeys, getProductDisplayFor, addManualProductEntry,
// TROUBLE_NOTE_COOLDOWN_DAYS, isTroubleShownRecently, recordTroubleShown
// moved to src/lib/products.js (Plan 37-02).

// Icon map for COLOUR_TYPES (replaces emoji strings in theme.js).
// Exported so feature-folder components (GardenSummaryCard, ColourPalette, etc.)
// can render colour-type glyphs without re-declaring the map.
export const COLOUR_TYPE_ICONS = { Flower2, Leaf, Apple, TreeDeciduous, Sparkles };

// LegalModal + PRIVACY_CONTENT + TERMS_CONTENT moved to src/modals/LegalModal.jsx
// (Plan 37-06; Open Question 1 resolved — content colocates with the modal).
// Re-exported via the transitional shim near top-of-file so existing imports
// from src/App.legal.test.jsx keep working until Plan 37-09 sweeps test imports.

// ConfirmModal moved to src/modals/ConfirmModal.jsx (Plan 37-06).


// View metadata — drives ViewHeader and is consistent with NAV_ITEMS
const VIEW_META = {
  year:    { icon: Calendar,       title: "Year at a Glance" },
  month:   { icon: CalendarDays,  title: "Monthly Tasks" },
  session: { icon: Shovel,        title: "Activity" },
  shed:    { icon: Package,       title: "Supplies" },
  palette: { icon: Palette,       title: "Living Colours" },
  plants:  { icon: Leaf,          title: "Plants" },
  ask:     { icon: MessageCircle, title: "Ask Tend" },
};
// Views where the area filter is contextually useful
const AREA_FILTER_VIEWS = ["year","month","palette","plants"];
// Exported so the NEW top-level views (YearView, MonthView, PlantsView)
// extracted in Plan 37-07 Task 4 can reuse the unified header pattern
// (icon + title + area filter) without re-implementing it. Stays in
// App.jsx per D-01 — closely coupled to VIEW_META and the App() filterArea
// state shape; moving it would create a wider import surface than keeping it.
export function ViewHeader({view,filterArea,setFilterArea,areaFilterOptions,headingRef,selectedMonth,potteringLabel,resolvedRegion}) {
  const meta = VIEW_META[view];
  const [isMobile, setIsMobile] = useState(()=>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 600px)').matches : false
  );
  useEffect(()=>{
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 600px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  },[]);
  if (!meta) return null;
  // PR #289 commit 2 — season-aware Year icon (hemisphere-aware via region).
  // Other views keep their static VIEW_META icon.
  const seasonForYear = view === 'year' ? getSeasonIcon(CURRENT_MONTH, resolvedRegion) : null;
  const Icon = seasonForYear ? seasonForYear.Icon : meta.icon;
  const iconColour = seasonForYear ? seasonForYear.tone : undefined;
  const showFilter = AREA_FILTER_VIEWS.includes(view) && areaFilterOptions && areaFilterOptions.length > 2;
  // Admin batch 2 commit 6 — Activity/Pottering title pulls from the same
  // helper as the tab label so the two surfaces never drift apart.
  const title = (view==="month"&&selectedMonth)
    ? `${MONTH_FULL[selectedMonth-1]} Tasks`
    : (view==="session" && potteringLabel) ? potteringLabel : meta.title;
  const active = filterArea !== "all";
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:20,paddingBottom:14,borderBottom:"1px solid var(--c-border)"}}>
      <Icon size={20} className="t-heading-shrink" style={iconColour ? { color: iconColour, stroke: iconColour } : undefined} aria-label={seasonForYear ? `Year — ${seasonForYear.label}` : undefined}/>
      <h2 ref={headingRef} tabIndex={-1} style={{fontFamily:"var(--font-family-display)",fontSize:"var(--font-h2)",color:"var(--c-heading)",margin:0,fontWeight:700,flex:1,outline:"none",WebkitTapHighlightColor:"transparent",boxShadow:"none"}}>{title}</h2>
      {showFilter&&(
        <span style={{position:"relative",display:"inline-flex",alignItems:"center",gap:isMobile?0:6,padding:isMobile?"8px":"8px 12px",borderRadius:20,border:"1px solid var(--c-border)",background:"var(--c-bg)",maxWidth:isMobile?undefined:200,cursor:"pointer",flexShrink:0}}>
          <Filter size={isMobile?16:14} fill={active?"currentColor":"none"} style={{color:active?"var(--c-heading)":"var(--c-text-3)",flexShrink:0}}/>
          <select value={filterArea} onChange={e=>setFilterArea(e.target.value)} aria-label="Filter by space"
            style={isMobile
              ? {position:"absolute",inset:0,opacity:0,width:"100%",height:"100%",border:"none",background:"transparent",cursor:"pointer",appearance:"none",WebkitAppearance:"none",fontSize:16}
              : {fontSize:"var(--font-body)",padding:0,border:"none",background:"transparent",color:active?"var(--c-heading)":"var(--c-text-3)",fontWeight:active?600:400,cursor:"pointer",outline:"none",appearance:"none",WebkitAppearance:"none",flex:1,minWidth:0}}>
            {areaFilterOptions.map(o=><option key={o.val} value={o.val}>{o.label}</option>)}
          </select>
        </span>
      )}
    </div>
  );
}

// storageKey, dismissKey, monthOffset moved to src/lib/storage.js (Plan 37-02)
// isDarkColour, fmtSize moved to src/lib/format.js (Plan 37-02)

// isValidPlant moved to src/lib/plants.js (Plan 37-02).

// computeEffectiveFeatures moved to src/lib/engagement.js (Plan 37-02).

// safeSetItem moved to src/lib/storage.js (Plan 37-02).
// Onboarding storage helpers (persistOnboardingProgress, markOnboardingComplete,
// persistOnboardField, persistOnboardArray, migrateGardenType, persistOnboardJSON)
// moved to src/lib/storage.js (Plan 37-02).

// Location helpers (UK_POSTCODE_RE, UK_POSTCODE_OUTWARD_RE, isValidUkPostcode,
// normaliseUkPostcode, outwardCode, requestGeolocation, reverseGeocode)
// moved to src/lib/location.js (Plan 37-02).

// persistOnboardJSON moved to src/lib/storage.js (Plan 37-02).

// Onboarding constants (ONBOARD_GARDEN_SPACES, ONBOARD_EXPERIENCE,
// ONB_SKIES, ONB_SKIES_DARK, ONB_AREA_PATH_FALLBACK, ONB_AREA_PATHS,
// ONB_GARDEN_SPACE_TO_AREA_LABEL, ONB_SPOTLIGHT_FALLBACKS, ONBOARD_PRESET_AREAS,
// GARDEN_SPACE_TO_AREA_LABEL, GARDEN_SPACE_TO_CUSTOM_AREA) and
// onbResolveSpotlightColors moved to src/onboarding/{constants,spotlight}.js
// (Plan 37-03).

// OnbClouds, OnbGround, OnbBee, OnbButterfly, OnbProgressRail, OnbTileArt,
// OnbPlotPlan, OnbFrame, OnboardingChip moved to src/onboarding/*.jsx
// (Plan 37-03).


// OnboardingWelcome moved to src/onboarding/OnboardingWelcome.jsx (Plan 37-03).

// OnboardingGardenSpaces moved to src/onboarding/OnboardingGardenSpaces.jsx (Plan 37-03).

// ONBOARD_PRESET_AREAS, GARDEN_SPACE_TO_AREA_LABEL, GARDEN_SPACE_TO_CUSTOM_AREA
// moved to src/onboarding/constants.js (Plan 37-03).

// OnboardingAreas moved to src/onboarding/OnboardingAreas.jsx (Plan 37-03).

// OnboardingSubSpaces moved to src/onboarding/OnboardingSubSpaces.jsx (Plan 37-03).

// OnboardingPlant moved to src/onboarding/OnboardingPlant.jsx (Plan 37-03).

// OnboardingTaskReveal moved to src/onboarding/OnboardingTaskReveal.jsx (Plan 37-03).

// OnboardingNudgeCard moved to src/home-panel/OnboardingNudgeCard.jsx (Plan 37-04).

// OnboardingWizard moved to src/onboarding/OnboardingWizard.jsx (Plan 37-03).

// safeSetItem moved to src/lib/storage.js (Plan 37-02).

// parseStatusResponse, reprobeAiStatus, fireAskBeacon, fireAffBeacon
// moved to src/lib/ai.js (Plan 37-02).

// Engagement helpers (UPGRADE_TRIGGERS, defaultUpgradePromptState,
// readUpgradePromptState, shouldShowUpgrade, defaultReviewPromptState,
// REPORTED_IMAGES_WINDOW_MS, readReportedImages, isImageReportSuppressed,
// readReviewPromptState, isEngagementEligible, shouldShowReviewPrompt,
// REVIEW_DESTINATIONS, getReviewDestination)
// moved to src/lib/engagement.js (Plan 37-02).
// djb2 moved to src/lib/format.js (Plan 37-02).

// darkenHex moved to src/lib/format.js (Plan 37-02).



// PARSE_TASK_KEYWORDS, PARSE_MONTH_ABBREVS, PARSE_MONTH_PATTERN
// moved to src/lib/ai.js (Plan 37-02 — Pitfall 1: travel with parseSchedule).

// ── Home Panel constants ───────────────────────────────────────────────────────
// Lucide icon component mapped to each plant type for the Garden Summary card
// Plant-type → Lucide icon map. Exported so feature-folder components
// (GardenSummaryCard, InspireCard, PlantImage…) render the same glyphs.
export const PLANT_TYPE_ICONS = {
  shrub:            Shrub,
  "evergreen shrub":Leaf,
  tree:             Trees,
  "fruit tree":     Trees,
  perennial:        Flower2,
  bulb:             Droplets,
  grass:            Barcode,
  climber:          ArrowUpDown,
  herb:             Sprout,
  annual:           Flower2,
  fern:             LeafyGreen,
  topiary:          Scissors,
  bamboo:           Barcode,
};

// GAP_RULES moved to src/home-panel/HomePanel.jsx (Plan 37-04 — sole consumer).

// Wiki image cache + fetch chain (IMGURL_STORE, IMGURL_CACHE_MAX, _storeImgUrl,
// setImageOverrides, getImageOverrides, isPlantPage, fetchWikiImage)
// moved to src/lib/wiki-image.js (Plan 37-02). Lazy-getter pattern used for
// the imgCache module-level singleton (Risk 10).

// normalisePendingPlant moved to src/lib/plants.js (Plan 37-02).

// wipeAllTendData and countAllTasks moved to src/lib/storage.js (Plan 37-02).

// Phase 31.1 Plan 10 — UAT2-GAP-05 rewrite. Each hint now carries an
// explicit length/structure directive so Haiku produces materially
// different replies per tier. Baseline phrase "Be concise, practical,
// and friendly" is ALSO removed tier-conditionally (see buildSystemPrompt
// below) to avoid contradicting the beginner tone.
// Legacy D-08.1 invariant preserved: a missing/empty/unknown experience
// value produces NO tone addition AND keeps the stock baseline.
// TONE_HINTS, buildSystemPrompt moved to src/lib/ai.js (Plan 37-02).

// ── PlantImage ────────────────────────────────────────────────────────────────
// Exported so cross-folder views (PlantCard, DivisionPlantCard, SessionRow,
// AskTend preview, BulkImportPanel, etc.) can render Wikipedia thumbnails
// without re-implementing the cache lookup + fade-in logic. Stays in App.jsx
// per D-01 — small surface, single canonical implementation.
export function PlantImage({plant,size=52}) {
  const [src,setSrc]=useState(null);
  useEffect(()=>{fetchWikiImage(plant.wiki,plant.latin).then(setSrc);},[plant.wiki,plant.latin]);
  const hue=(plant.id*67)%360;
  if (src) return <img src={src} alt={plant.name} width={size} height={size} style={{objectFit:"cover",borderRadius:size>40?8:4,flexShrink:0}} onError={()=>setSrc(null)}/>;
  const Icon=PLANT_TYPE_ICONS[plant.type]||Leaf;
  return (
    <div style={{position:"relative",width:size,height:size,borderRadius:size>40?8:4,background:`hsl(${hue},35%,65%)`,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
      <Icon size={size>40?size*0.45:size*0.55}/>
      {size>40&&<Pencil size={10} style={{position:"absolute",bottom:3,right:3,opacity:0.7}}/>}
    </div>
  );
}

// ── TaskBadge ─────────────────────────────────────────────────────────────────
// Exported so cross-folder views (PlantCard, PlantDirectoryCard, AreaManager)
// render the small task-type pill without duplicating the Lucide+CSS-var
// recipe. Stays in App.jsx per D-01.
export function TaskBadge({type}) {
  const k=(type||"note").replace(/\//g,"-");
  return <span style={{background:`var(--cb-${k}-bg,#f5f5f5)`,color:`var(--cb-${k}-text,#555)`,border:`1px solid var(--cb-${k}-border,#ccc)`,borderRadius:12,padding:"3px 10px",fontSize:'0.8125rem',fontWeight:600,textTransform:"capitalize",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:4}}><TaskIcon type={type} size={12}/>{type}</span>;
}

// ── ClearableInput (UX-10) ────────────────────────────────────────────────────
// Drop-in wrapper around a native <input> that renders a × clear button when
// the field has content. Parent keeps its own value/onChange; the wrapper just
// overlays the ×. For type="text" and unspecified-type controlled inputs.
export const ClearableInput = forwardRef(function ClearableInput(
  { value, onChange, onClear, style, wrapperStyle, ...rest },
  ref
) {
  const show = typeof value === "string" && value.length > 0;
  const inputStyle = {
    ...style,
    paddingRight: show ? 36 : (style?.paddingRight ?? undefined),
    width: style?.width ?? "100%",
    boxSizing: style?.boxSizing ?? "border-box",
  };
  const handleClear = () => {
    if (typeof onChange === "function") {
      onChange({ target: { value: "" }, currentTarget: { value: "" } });
    }
    if (typeof onClear === "function") onClear();
  };
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", ...(wrapperStyle || {}) }}>
      <input ref={ref} value={value} onChange={onChange} style={inputStyle} {...rest} />
      {show && (
        <button
          type="button"
          onMouseDown={(e)=>e.preventDefault()}
          onClick={handleClear}
          aria-label="Clear field"
          title="Clear"
          style={{
            position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
            background: "var(--c-surface,#eee)", border: "1px solid var(--c-border)", borderRadius: "50%",
            cursor: "pointer", padding: 0, width: 22, height: 22,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--c-text)",
          }}
        >
          <X size={14} strokeWidth={2.5}/>
        </button>
      )}
    </span>
  );
});

// ── CSV Export ────────────────────────────────────────────────────────────────
function exportCSV(plants,areas) {
  const aMap=Object.fromEntries(areas.map(a=>[a.id,a.name]));
  const rows=plants.map(p=>{
    const locs=(p.locations||[]).map(l=>`${aMap[l.areaId]||l.areaId}${l.subArea?" › "+l.subArea:""}`).join("; ");
    return[p.name,p.variety||"",p.latin||"",p.type||"",locs,p.count||1,Object.keys(p.tasks||{}).map(m=>MONTHS[parseInt(m)-1]).join(", ")];
  });
  const csv=[["Common Name","Variety","Latin Name","Type","Locations","Count","Task Months"],...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
  const a=document.createElement("a"); a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"})); a.download="tend-plants.csv"; a.click();
}

// rankInspireCandidates + COMPLEMENTARY_COLOUR_PAIRS moved to
// src/home-panel/InspireCard.jsx (Plan 37-04 — co-located with their sole
// consumer). _hashStr, _dayOfYear, _slugifyInspire stay in src/lib/format.js.

// ── Data version — increment this when INITIAL_PLANTS or INITIAL_AREAS change
// v9: 2026-04-20 — Front Garden INITIAL_AREAS sub-area list expanded to match
// Back Garden (6 presets: Main Border, Shady Border, Pots & Containers,
// Lawn Edge, Lawn, Wall / Fence). UAT Gap 6b. Existing user stores get the
// new sub-areas merged in automatically via the INITIAL_AREAS sub-area merge
// pass in the migration logic (step 5 per CLAUDE.md §"DATA_VERSION migration").
// v8: add sun/soil/hardiness to sample plants (ENG-01)
const DATA_VERSION = 9;

// normalisePlant moved to src/lib/plants.js (Plan 37-02).

// extractPlantAdd, extractPlantAddBatch moved to src/lib/ai.js (Plan 37-02).
// (Lifted to module scope first in commit b134434 — Pitfall 2 prep — then moved.)

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  // Phase 40 Plan 02 Task 1 — /auth/callback route guard. The static fallback
  // at public/auth/callback.html wins direct navigations via the Vercel rewrite;
  // this guard handles in-app navigations (e.g. browsers that follow SPA rewrites).
  // Compares against base + 'auth/callback' so dev (under /garden-planner/) and
  // prod (under /tend/ or /) both resolve to the AuthCallback component.
  if (typeof window !== 'undefined') {
    const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
    if (
      window.location.pathname === '/auth/callback' ||
      window.location.pathname === `${base}/auth/callback`
    ) {
      return <AuthCallback />;
    }
  }
  const [plants,     setPlants]     = useState(()=>{
    try {
      const storedVersion = parseInt(localStorage.getItem("tend:dataversion")||"0");
      const s = localStorage.getItem("tend:plants");
      if (!s) return []; // new user — start empty; onboarding wizard adds the first plant
      const raw = JSON.parse(s);
      // Schema validation — guard against corrupt or misshapen localStorage data
      // isValidPlant — module-level export (used here and in tests)
      if (!Array.isArray(raw) || !raw.every(isValidPlant)) {
        console.warn("tend: stored plant data failed schema validation — resetting to defaults");
        return INITIAL_PLANTS;
      }
      let stored = raw;
      if (storedVersion < DATA_VERSION) {
        const knownAreaIds = new Set(INITIAL_AREAS.map(a=>a.id));
        // 1. Normalise all stored plants to use locations[]
        stored = stored.map(normalisePlant);
        // 2. Remove the old duplicate bay tree record (id 44) — was merged into id 43
        stored = stored.filter(p=>p.id!==44);
        // 3. Merge in any plants from INITIAL_PLANTS not already present (by id)
        const storedIds = new Set(stored.map(p=>p.id));
        const newPlants = INITIAL_PLANTS.filter(p=>!storedIds.has(p.id));
        // 4. For plants already present, update their locations and backfill dimensions
        stored = stored.map(p=>{
          const canonical = INITIAL_PLANTS.find(ip=>ip.id===p.id);
          if (!canonical) return p;
          let updated = {...p};
          // Merge locations: add any from canonical not already in stored
          if (canonical.locations && p.locations) {
            const existingKeys = new Set(p.locations.map(l=>`${l.areaId}::${l.subArea}`));
            const newLocs = canonical.locations.filter(l=>!existingKeys.has(`${l.areaId}::${l.subArea}`));
            if (newLocs.length) updated = {...updated, locations:[...p.locations,...newLocs]};
          }
          // Backfill height/spread from canonical if not already set
          if (p.height==null && canonical.height!=null) updated = {...updated, height:canonical.height};
          if (p.spread==null && canonical.spread!=null) updated = {...updated, spread:canonical.spread};
          // ENG-01 (v8): backfill growing-condition fields when canonical provides them
          if (p.sun==null       && canonical.sun!=null)       updated = {...updated, sun:canonical.sun};
          if (p.soil==null      && canonical.soil!=null)      updated = {...updated, soil:canonical.soil};
          if (p.hardiness==null && canonical.hardiness!=null) updated = {...updated, hardiness:canonical.hardiness};
          return updated;
        });
        let merged = [...stored, ...newPlants];
        // 5. Generic deduplication — keep first occurrence of each id
        const seen = new Set();
        merged = merged.filter(p => seen.has(p.id) ? false : seen.add(p.id));
        // 6. Strip locations referencing unknown areas (guards against stale area IDs)
        merged = merged.map(p=>({...p, locations:(p.locations||[]).filter(l=>knownAreaIds.has(l.areaId))}));
        return merged;
      }
      return stored;
    } catch { return INITIAL_PLANTS; }
  });
  const [areas,      setAreas]      = useState(()=>{
    try {
      const storedVersion = parseInt(localStorage.getItem("tend:dataversion")||"0");
      const s = localStorage.getItem("tend:areas");
      if (!s) return []; // new user — start empty; onboarding wizard collects areas
      if (storedVersion < DATA_VERSION) {
        // Merge: add any sub-areas from INITIAL_AREAS not already present
        const stored = JSON.parse(s);
        return INITIAL_AREAS.map(ia=>{
          const existing = stored.find(a=>a.id===ia.id);
          if (!existing) return ia;
          const mergedSubs = [...new Set([...(existing.subAreas||[]),...(ia.subAreas||[])])];
          return {...existing, subAreas:mergedSubs};
        });
      }
      return JSON.parse(s);
    } catch { return INITIAL_AREAS; }
  });
  const [completions,setCompletions]= useState(()=>{ try{const s=localStorage.getItem("tend:completions"); return s?JSON.parse(s):{};}catch{return {};} });
  const [inventory,  setInventory]  = useState(()=>{ try{const s=localStorage.getItem("tend:inventory"); return s?JSON.parse(s):{};}catch{return {};} });
  const [plantNotes, setPlantNotes] = useState(()=>{ try{const s=localStorage.getItem("tend:notes"); return s?JSON.parse(s):{};}catch{return {};} });
  const [upgradePromptState, setUpgradePromptState] = useState(()=>readUpgradePromptState());
  const [serverKeyReady, setServerKeyReady] = useState(null); // null=unknown, true=server key set, false=not set
  const [commercialFeaturesEnabled, setCommercialFeaturesEnabled] = useState(false); // Phase 38 D-17 — default false (Hobby beta)
  // Phase 34 / RES-01 / D-01 — three-state AI availability machine.
  // 'ready'    = serverKeyReady === true and no recent in-flight failure
  // 'degraded' = a recent AI request returned non-2xx but the latest probe succeeded
  // 'down'     = the latest proxy.php?status=1 probe returned ready:false or failed
  const [aiStatus, setAiStatus] = useState('ready');
  const [lastAiFailureAt, setLastAiFailureAt] = useState(null); // epoch-ms of most recent in-flight non-2xx
  const [maxPlants,      setMaxPlants]      = useState(1000);  // server-configurable cap; default 1000
  const [features, setFeatures] = useState({ask_tend:false, ai_schedule:false, bulk_import:false, colour_enrichment:false, ask_tend_add:false, upgrade_prompts:false, review_prompt:false});
  const [featureTier, setFeatureTier] = useState('free'); // for dev mode display only
  // Public flags from admin_flags table via /feature-flags Worker endpoint (5-min cache)
  const [publicFlags, setPublicFlags] = useState(()=>{ try{ const r=localStorage.getItem('tend:publicFlagsCache'); if(r){const {ts,flags}=JSON.parse(r); if(Date.now()-ts<5*60*1000) return flags;} }catch{} return {}; });
  const [isOnline, setIsOnline] = useState(()=>navigator.onLine);
  useEffect(()=>{
    const up=()=>setIsOnline(true);
    const dn=()=>setIsOnline(false);
    window.addEventListener("online",up);
    window.addEventListener("offline",dn);
    return ()=>{ window.removeEventListener("online",up); window.removeEventListener("offline",dn); };
  },[]);

  // PR #295 / Issue #276 — splash-mount version check.
  // When a new build deploys, an installed PWA can otherwise hit a hybrid
  // old-bundle + new-asset state mid-session. Strategy:
  //   - Track session start timestamp (App-mount).
  //   - If `onNeedRefresh` fires within the first AUTO_UPDATE_WINDOW_MS, the
  //     user has effectively just opened the PWA — auto-skipWaiting + reload
  //     before they see the home view.
  //   - If a new SW arrives later (mid-session), fall back to the existing
  //     banner so we don't surprise the user mid-task.
  const sessionStartRef = useRef(Date.now());
  const [updating, setUpdating] = useState(false);
  const AUTO_UPDATE_WINDOW_MS = 3000;
  const AUTO_UPDATE_INDICATOR_DELAY_MS = 500;
  const { needRefresh: [needRefresh], updateServiceWorker } = useRegisterSW({
    onRegisteredSW(url, r) { r && setInterval(() => r.update(), 60 * 60 * 1000); },
    onNeedRefresh() {
      const elapsed = Date.now() - sessionStartRef.current;
      if (elapsed > AUTO_UPDATE_WINDOW_MS) return; // mid-session — defer to banner
      // Splash window — auto-update. Show "Updating Tend…" only if the
      // skipWaiting + reload path takes long enough to be noticeable
      // (>=500ms); fast paths skip the indicator entirely.
      const indicator = setTimeout(() => setUpdating(true), AUTO_UPDATE_INDICATOR_DELAY_MS);
      try {
        updateServiceWorker(true);
      } catch {
        clearTimeout(indicator);
        setUpdating(false);
      }
    },
  });

  const [devModeAllowed, setDevModeAllowed] = useState(false); // server flag — true only on whitelisted deployments
  const [devModeActive,  setDevModeActive]  = useState(()=>localStorage.getItem("tend:devmode")==="true");
  const [simulatePremium, setSimulatePremium] = useState(false); // dev mode only — overrides all feature flags to premium
  const [simulateUpgradePrompts, setSimulateUpgradePrompts] = useState(()=>localStorage.getItem('tend:simulateUpgradePrompts')==='true'); // D-16 dev preview

  // Phase 27 ENG-03 — completion-day tracker with bulk-mark-done defence (D-13)
  const [completionDates, setCompletionDates] = useState(() => {
    try {
      const raw = localStorage.getItem('tend:completionDates');
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? new Set(arr) : new Set();
    } catch { return new Set(); }
  });

  // Phase 27 ENG-03 — distinct-day session log (D-12 trigger C, D-18)
  const [sessions, setSessions] = useState(() => {
    try {
      const raw = localStorage.getItem('tend:sessions');
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  });

  // Phase 27 ENG-03 — review-prompt cooldown + cap state (D-16)
  const [reviewPromptState, setReviewPromptState] = useState(() => readReviewPromptState());

  // Phase 34 IMG-01 / D-08 — tend:reportedImages 30-day per-plant suppress for
  // the "Report wrong image" CTA. Read prunes entries older than 30 days, so
  // the in-memory array stays bounded over time.
  const [reportedImages, setReportedImages] = useState(() => readReportedImages());
  // Phase 34 IMG-01 / D-07 — transient toast state for the report flow.
  // null | 'success' | 'error'; auto-cleared after ~4.5s by handleReportImage.
  const [reportToast, setReportToast] = useState(null);

  // Phase 27 ENG-03 — dev-only simulate toggle (ships in v2.2)
  const [simulateReviewPrompt, setSimulateReviewPrompt] = useState(() => localStorage.getItem('tend:simulateReviewPrompt') === 'true');
  // Phase 31.1 Plan 05 / UAT Gap 3 — dev-only simulate toggles that force-show
  // the Inspire Me card and the Featured Supplier card on the Home Panel,
  // bypassing the dismissed-state and month/rotation gates. Mirror the
  // simulateReviewPrompt hygiene — cleared on load when devModeAllowed=false
  // via the proxy.php?status=1 callback below (see the if(!allowed) branch).
  const [simulateInspireMe, setSimulateInspireMe] = useState(() => localStorage.getItem('tend:simulateInspireMe') === 'true');
  const [simulateFeaturedSupplier, setSimulateFeaturedSupplier] = useState(() => localStorage.getItem('tend:simulateFeaturedSupplier') === 'true');
  // Phase 27 Plan 03 Gap 3 — inline review form modal state
  const [showReviewForm, setShowReviewForm] = useState(false);
  // Session-level suppression so dismiss/CTA hides the card immediately even when
  // forcePreview would otherwise bypass the dismissedAt/mutedAt gates. Reset by
  // resetPromptStateOnDevToggle('review') so OFF→ON re-shows the card.
  const [reviewCardClosed, setReviewCardClosed] = useState(false);

  const [showPremium,    setShowPremium]    = useState(false);
  const [showSettings,   setShowSettings]   = useState(false);
  // Phase 32 D-06 — events viewer modal visibility (dev mode only)
  const [showEventsViewer, setShowEventsViewer] = useState(false);
  // Region — one of 'GB'|'DE'|'FR'|'NL' or 'auto' (sentinel that re-derives).
  // detectRegion() handles first-load persistence and 'auto' re-derivation.
  // resolvedRegion initialised first so detectRegion() persists the derived code
  // before regionPref reads localStorage (WR-02).
  const [resolvedRegion, setResolvedRegion] = useState(() => detectRegion());
  const [regionPref, setRegionPref] = useState(() => {
    try { return localStorage.getItem("tend:region") || "auto"; }
    catch (_) { return "auto"; }
  });

  // When user changes region preference, persist and recompute resolved region.
  function changeRegion(next) {
    setRegionPref(next);
    try {
      localStorage.setItem("tend:region", next);
    } catch (_) {
      // Silent — matches the affiliates.js logger pattern (D-13).
    }
    // Derive resolved region from the chosen value directly so it is correct
    // even when the localStorage write fails (private mode / quota exceeded) (WR-01).
    const derived = next === 'auto'
      ? resolveLangToRegion((typeof navigator !== 'undefined' && navigator.language) || 'en-GB')
      : (AFFILIATE_REGIONS[next] ? next : 'GB');
    setResolvedRegion(derived);
  }
  const [showMenu,     setShowMenu]     = useState(false);
  const [showAbout,    setShowAbout]    = useState(false);
  const [legalModal,   setLegalModal]   = useState(null); // null | 'privacy' | 'terms'
  const [showFeedback, setShowFeedback] = useState(false);
  const [showShare,    setShowShare]    = useState(false);
  const [showAchievement, setShowAchievement] = useState(false);
  // Phase 40 Plan 02 Task 4 — Sign-in modal trigger from Home Panel SaveToCloudCard / Settings.
  const [showSignInModal, setShowSignInModal] = useState(false);
  // Phase 40 Plan 02 Task 4 — Auth context surfaces firstSignInState + syncedToastVisible.
  const auth = useSession();
  const [confirmModal, setConfirmModal] = useState(null); // null | {title, message, confirmLabel, onConfirm}
  const [askTendMessages, setAskTendMessages] = useState(null); // persists Ask Tend chat across view switches
  // Text-size preference (#248). 4 levels: 0.875 / 1 / 1.125 / 1.25 — applied
  // via the --text-scale CSS custom property on <html>. The rem migration
  // (PR #272) is mathematically identical to the original px values at
  // scale=1; this picker introduces the multiplier.
  const [textSize, setTextSize] = useState(() => {
    const raw = localStorage.getItem('tend:textsize');
    const valid = ['0.875', '1', '1.125', '1.25'];
    return valid.includes(raw) ? raw : '1';
  });
  useEffect(() => {
    document.documentElement.style.setProperty('--text-scale', textSize);
    try { localStorage.setItem('tend:textsize', textSize); } catch {}
  }, [textSize]);

  // Dark mode preference: "off" | "on" | "auto" (absent = auto). Per D-05, D-07.
  const [darkModePref, setDarkModePref] = useState(() => {
    return localStorage.getItem("tend:darkmode") || "auto";
  });
  const [darkMode, setDarkMode] = useState(() => {
    const pref = localStorage.getItem("tend:darkmode") || "auto";
    if (pref === "on") return true;
    if (pref === "off") return false;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });

  // Media query listener — only active when pref is "auto" (per D-06, D-08).
  useEffect(() => {
    // Per D-06: removed the localStorage.removeItem("tend:darkmode") call
    if (darkModePref !== "auto") return; // On/Off = static, no listener needed
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    setDarkMode(mq.matches); // sync immediately when switching to auto
    const handler = (e) => setDarkMode(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [darkModePref]);

  // Cycle: off -> auto -> on -> off (per D-01)
  function cycleDarkMode() {
    const next = darkModePref === "off" ? "auto" : darkModePref === "auto" ? "on" : "off";
    setDarkModePref(next);
    if (next === "auto") {
      localStorage.removeItem("tend:darkmode"); // absent = auto per D-05
    } else {
      localStorage.setItem("tend:darkmode", next); // per D-07
    }
    if (next === "on") setDarkMode(true);       // per D-09
    if (next === "off") setDarkMode(false);      // per D-10
    // "auto" case handled by the useEffect above
  }

  const [view,           setViewRaw]       = useState(() =>
    import.meta.env.DEV && new URLSearchParams(window.location.search).has('admin_screenshot_bypass')
      ? 'admin' : 'year'
  );
  // Phase 32 D-01 — view_switch wrapper. Single instrumentation point covers
  // every nav pill, drawer item, mobile-menu link, and programmatic setView
  // call. PII rule: only the view-name strings (year/month/plants/etc) are
  // logged — these are app-internal enums, never user content.
  const setView = useCallback((next) => {
    setViewRaw(prev => {
      if (typeof next === 'function') {
        const resolved = next(prev);
        if (resolved !== prev) logEvent('view_switch', { from: prev, to: resolved });
        return resolved;
      }
      if (next !== prev) logEvent('view_switch', { from: prev, to: next });
      return next;
    });
  }, []);
  const [selectedMonth,  setSelectedMonth] = useState(CURRENT_MONTH);
  const [filterTypes,    setFilterTypes]   = useState(new Set());
  const [filterArea,     setFilterArea]    = useState("all");
  const [showDone,       setShowDone]       = useState(false);
  const [searchQuery,    setSearchQuery]   = useState("");
  const [expandedPlantId, setExpandedPlantId] = useState(null);
  const [showAddForm,    setShowAddForm]   = useState(false);
  const [showImport,     setShowImport]    = useState(false);
  const [importMode,     setImportMode]    = useState("paste");
  const [showAreaMgr,    setShowAreaMgr]   = useState(false);
  const [showCapNotice,  setShowCapNotice] = useState(false);
  const [storageWarning, setStorageWarning] = useState(null);
  const [sessionTaskType,setSessionTaskType]=useState(null);
  const [showAddSheet,   setShowAddSheet]  = useState(false);
  const [sortOrder,      setSortOrder]     = useState(()=>localStorage.getItem("tend:sortorder")||"name-az");
  const [scrollToType,   setScrollToType] = useState(null);
  const [newPlant,       setNewPlant]      = useState({name:"",variety:"",latin:"",type:"",count:1,wiki:"",area:INITIAL_AREAS[0].id,subArea:"",height:"",spread:""});

  // Onboarding state (Phase 21 / ONB-01)
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0); // 0..3 — 0=Welcome, 1=Areas, 2=Plant, 3=TaskReveal
  const [onboardData, setOnboardData] = useState({ gardenSpaces: [], experience: "", location: "" });
  const [onboardSignal, setOnboardSignal] = useState(0); // bump to re-derive showOnboardNudge after localStorage writes

  // 21-04: One-way migration of legacy tend:gardenType → tend:gardenSpaces.
  // Runs BEFORE the prefill effect so prefill reads post-migration values.
  useEffect(() => { migrateGardenType(); }, []);

  // PR #306 + PR #307 — visualViewport sync for the bottom-nav.
  //
  // iOS Safari 15+ anchors `position: fixed; bottom: 0` to the visual viewport
  // *most* of the time, but documented quirks exist:
  //   - iOS 17 PWA: fixed elements drift after a while (Apple Forum 744327)
  //   - iOS 26: VisualViewport.offsetTop doesn't reset on keyboard dismiss (800154)
  //   - On-screen keyboard: visual viewport shrinks; layout viewport doesn't —
  //     fixed elements anchored to layout-bottom end up under the keyboard.
  //
  // The hook writes a CSS custom property representing the gap between the
  // layout viewport bottom and the visual viewport bottom. The bottom-nav uses
  // `bottom: var(--vv-bottom-offset, 0)` so it tracks the visual viewport in
  // every case the platform's automatic anchoring fails.
  //
  // PR #307 (bug 1): drop the `scroll` listener + drop the `offsetTop` term.
  // Rubber-band overscroll on iOS fires `scroll` (not `resize`) and produces a
  // positive `visualViewport.offsetTop` while the height stays unchanged — the
  // old calculation interpreted that as "URL bar showed up by N px" and
  // pushed the footer up. The cases we actually care about (URL bar collapse,
  // keyboard show/hide) all fire `resize`, so resize-only gates rubber-band
  // out. Using just the height delta also means the offset is always
  // non-negative — no need to clamp scroll-induced positives.
  //
  // PR #307 (bug 2 / first-paint flash): useLayoutEffect runs synchronously
  // after the DOM commit and before the browser paints. With useEffect there
  // was a one-frame window where the nav rendered at `bottom: 0` (default
  // var) BEFORE the hook ran — manifesting as a white strip below the nav
  // on iOS Safari first load (URL bar visible, layout viewport > visual,
  // nav anchored below the visible bottom). useLayoutEffect closes that
  // window.
  useLayoutEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const update = () => {
      const offset = Math.max(0, window.innerHeight - vv.height);
      document.documentElement.style.setProperty(
        '--vv-bottom-offset',
        `${offset}px`,
      );
    };
    vv.addEventListener('resize', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
    };
  }, []);

  // First-load: auto-launch wizard for brand-new users (no tend:onboarded, no progress yet — D-09)
  // Returning users with plant data but no `tend:onboarded` flag (pre-Phase-21 users, CSV-import
  // users, users who dismissed without progress) get the flag set retroactively and skip the wizard.
  useEffect(() => {
    try {
      const action = resolveOnboardingFirstLoadAction();
      if (action === 'mark-done') {
        markOnboardingComplete();
      } else if (action === 'open') {
        setIsOnboardingOpen(true);
        setOnboardingStep(0);
      }
      // Pre-fill Welcome fields from existing keys if present (replay case)
      // T-21-04-02: defensive parse — fall back to [] on any malformed payload
      let gs = [];
      try {
        const raw = localStorage.getItem('tend:gardenSpaces');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) gs = parsed.filter(v => typeof v === 'string');
        }
      } catch {}
      const ex = localStorage.getItem('tend:experience') || "";
      const lo = localStorage.getItem('tend:location') || "";
      setOnboardData({ gardenSpaces: gs, experience: ex, location: lo });
    } catch {}
  }, []);

  const handleOnboardingDismiss = () => {
    // WR-03: only persist progress when the user has made meaningful progress.
    // Without this guard, skipping the Welcome step still writes tend:onboardingProgress
    // and surfaces a "Finish setting up Tend" nudge that misrepresents the state.
    const hasProgress = onboardingStep > 0
      || (Array.isArray(onboardData.gardenSpaces) && onboardData.gardenSpaces.length > 0)
      || !!onboardData.experience
      || (onboardData.location && onboardData.location.trim() !== "");
    if (hasProgress) persistOnboardingProgress(onboardingStep);
    setIsOnboardingOpen(false);
    setOnboardSignal(s => s + 1);
  };
  const handleOnboardingComplete = () => {
    // Phase 32 D-01 — onboarding_complete fires once per first-run wizard finish
    logEvent('onboarding_complete');
    markOnboardingComplete();
    setIsOnboardingOpen(false);
    setOnboardSignal(s => s + 1);
  };
  const handleShowTour = () => { setOnboardingStep(0); setIsOnboardingOpen(true); };

  // Home Panel nudge (D-09) — show when partial progress exists and user is not already onboarded
  const showOnboardNudge = useMemo(() => {
    try {
      const onboarded = localStorage.getItem('tend:onboarded');
      const progress  = localStorage.getItem('tend:onboardingProgress');
      const dismissed = localStorage.getItem('tend:onboardNudgeDismissed');
      return !onboarded && !!progress && !dismissed && !isOnboardingOpen;
    } catch { return false; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboardingOpen, onboardSignal]);

  const handleResumeOnboarding = () => {
    let step = 0;
    try {
      const raw = localStorage.getItem('tend:onboardingProgress');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Number.isInteger(parsed.step) && parsed.step >= 0 && parsed.step <= 3) {
          step = parsed.step;
        }
      }
    } catch { /* T-21-07: malformed JSON → fall back to step 0 */ }
    setOnboardingStep(step);
    setIsOnboardingOpen(true);
  };

  const handleDismissOnboardNudge = () => {
    // Hide the nudge without claiming onboarding is complete (WR-01).
    // Uses a dedicated key so tend:onboarded / tend:onboardingProgress are preserved —
    // the user can still resume via Settings → Welcome tour, and analytics / gated
    // features that key off tend:onboarded don't get a false positive.
    try { localStorage.setItem('tend:onboardNudgeDismissed', '1'); } catch {}
    setOnboardSignal(s => s + 1);
  };

  useEffect(()=>{
    if (!Array.isArray(plants) || !plants.every(isValidPlant)) {
      console.warn('tend: refusing to write invalid plant data to localStorage');
      return;
    }
    safeSetItem('tend:plants', JSON.stringify(plants), setStorageWarning);
  },[plants]);
  useEffect(()=>{ safeSetItem('tend:areas', JSON.stringify(areas), setStorageWarning); },[areas]);
  useEffect(()=>{ safeSetItem('tend:completions', JSON.stringify(completions), setStorageWarning); },[completions]);
  useEffect(()=>{ safeSetItem('tend:inventory', JSON.stringify(inventory), setStorageWarning); },[inventory]);
  useEffect(()=>{ safeSetItem('tend:notes', JSON.stringify(plantNotes), setStorageWarning); },[plantNotes]);
  useEffect(()=>{ safeSetItem('tend:upgradePrompts', JSON.stringify(upgradePromptState), setStorageWarning); },[upgradePromptState]);
  // Fetch public feature flags from admin_flags via Worker on mount (nav.pottering etc.)
  useEffect(()=>{ getPublicFlags().then(f=>{ if(f&&typeof f==='object') setPublicFlags(f); }).catch(()=>{}); },[]);

  // Single source of truth for the Activity / Pottering label — drives both
  // the tab nav AND the view heading (admin batch 2 commit 6).
  const potteringLabel = getPotteringTabLabel(!!publicFlags['nav.pottering'], resolvedRegion);

  useEffect(()=>{
    fetch(`${getApiBase()}/proxy.php?status=1`).then(r=>r.json()).then(d=>{
      const { serverKeyReady, devModeAllowed: allowed, commercialFeaturesEnabled: commercial } = parseStatusResponse(d);
      setServerKeyReady(serverKeyReady);
      setDevModeAllowed(allowed);
      setCommercialFeaturesEnabled(commercial); // Phase 38 D-17
      if(d.maxPlants&&Number.isInteger(d.maxPlants)&&d.maxPlants>=1) setMaxPlants(d.maxPlants);
      if(d.features&&typeof d.features==='object') setFeatures(d.features);
      if(d.tier) setFeatureTier(d.tier);
      // Phase 34 / RES-01 / D-01 — derive aiStatus from probe response.
      // Probe says-not-ready -> down. Probe ready BUT a recent (<5min) failure
      // happened -> degraded. Otherwise ready.
      if (!serverKeyReady) {
        setAiStatus('down');
      } else if (lastAiFailureAt && (Date.now() - lastAiFailureAt) < 5 * 60 * 1000) {
        setAiStatus('degraded');
      } else {
        setAiStatus('ready');
      }
      // If this deployment doesn't allow dev mode, clear any stale stored state
      if(!allowed){
        localStorage.removeItem("tend:devmode"); setDevModeActive(false);
        // Phase 31.1 Plan 05 / UAT Gap 3 — hygiene: clear the dev-only simulate*
        // keys so a local-storage-injected flag cannot take effect in production.
        localStorage.removeItem("tend:simulateInspireMe");        setSimulateInspireMe(false);
        localStorage.removeItem("tend:simulateFeaturedSupplier"); setSimulateFeaturedSupplier(false);
      }
    }).catch(()=>{
      // Phase 34 / RES-01 / D-01 — probe network failure means we cannot reach
      // the proxy at all; treat as down so the banner appears.
      setServerKeyReady(false);
      setAiStatus('down');
    });
  },[]);
  // Phase 34 IMG-01 / D-06 — fetch operator-managed image overrides on app load.
  // Cache-busted by build hash so a one-line JSON edit propagates to users on
  // next reload via service-worker NetworkFirst. Cache invalidation: drop any
  // tend:imgurls entry whose key matches an override key so the next resolver
  // call re-resolves through Phase 0.
  useEffect(() => {
    const v = (typeof __GIT_HASH__ !== 'undefined') ? __GIT_HASH__ : 'dev';
    fetch(`plant-image-overrides.json?v=${v}`)
      .then(r => r.ok ? r.json() : {})
      .then(map => {
        if (!map || typeof map !== 'object' || Array.isArray(map)) return;
        setImageOverrides(map);
        try {
          const cachedRaw = localStorage.getItem(IMGURL_STORE);
          if (!cachedRaw) return;
          const cached = JSON.parse(cachedRaw);
          if (!cached || typeof cached !== 'object') return;
          let changed = false;
          // The cache key in fetchWikiImage is `latin || wiki`. Drop any cached
          // entry whose key matches an override key so Phase 0 re-resolves.
          for (const overrideKey of Object.keys(map)) {
            if (overrideKey in cached) {
              delete cached[overrideKey];
              delete imgCache[overrideKey];
              changed = true;
            }
          }
          if (changed) {
            safeSetItem(IMGURL_STORE, JSON.stringify(cached), setStorageWarning);
          }
        } catch {}
      })
      .catch(() => setImageOverrides({}));
  }, []);
  useEffect(()=>{
    if(devModeActive) safeSetItem('tend:devmode', 'true', setStorageWarning);
    else localStorage.removeItem("tend:devmode");
  },[devModeActive]);
  // Auto-open About dialog on load when developer mode is active (instant version check)
  useEffect(()=>{
    if(devModeActive) setShowAbout(true);
  },[]);
  // Persist simulateUpgradePrompts toggle state (D-16)
  useEffect(()=>{
    if(simulateUpgradePrompts) safeSetItem('tend:simulateUpgradePrompts', 'true', setStorageWarning);
    else { try{ localStorage.removeItem('tend:simulateUpgradePrompts'); }catch{} }
  },[simulateUpgradePrompts]);

  // Phase 27 ENG-03 — persistence effects for engagement tracker state
  useEffect(() => { safeSetItem('tend:completionDates', JSON.stringify([...completionDates]), setStorageWarning); }, [completionDates]);
  useEffect(() => { safeSetItem('tend:sessions', JSON.stringify(sessions), setStorageWarning); }, [sessions]);
  useEffect(() => { safeSetItem('tend:reviewPromptState', JSON.stringify(reviewPromptState), setStorageWarning); }, [reviewPromptState]);
  // Phase 34 IMG-01 / D-08 — persist the 30-day per-plant report suppression list.
  useEffect(() => { safeSetItem('tend:reportedImages', JSON.stringify(reportedImages), setStorageWarning); }, [reportedImages]);
  useEffect(() => {
    if (simulateReviewPrompt) safeSetItem('tend:simulateReviewPrompt', 'true', setStorageWarning);
    else { try { localStorage.removeItem('tend:simulateReviewPrompt'); } catch {} }
  }, [simulateReviewPrompt]);

  // Phase 31.1 Plan 05 / UAT Gap 3 — persistence for the two new dev-only simulate toggles.
  useEffect(() => {
    if (simulateInspireMe) safeSetItem('tend:simulateInspireMe', 'true', setStorageWarning);
    else { try { localStorage.removeItem('tend:simulateInspireMe'); } catch {} }
  }, [simulateInspireMe]);
  useEffect(() => {
    if (simulateFeaturedSupplier) safeSetItem('tend:simulateFeaturedSupplier', 'true', setStorageWarning);
    else { try { localStorage.removeItem('tend:simulateFeaturedSupplier'); } catch {} }
  }, [simulateFeaturedSupplier]);

  // Append today's ISO date to sessions on mount (de-duped + capped at 30).
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    setSessions(prev => {
      if (prev.includes(today)) return prev;
      const next = [...prev, today];
      return next.length > 30 ? next.slice(next.length - 30) : next;
    });
  }, []); // mount-only

  // Phase 32 D-05 — visibilitychange listener wired now; flushEvents() is a
  // no-op stub in v2.3 (src/data/events.js). v3.0 Phase 999.3 swaps the body
  // to drain tend:events to Supabase using tend:eventsSyncCursor. Wiring
  // ships now so the cutover is body-only — zero call-site changes during a
  // high-stakes cloud migration. Mirrors the dark-mode media listener
  // cleanup pattern at src/App.jsx:3294-3303.
  useEffect(() => {
    const onVis = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        flushEvents();
      }
    };
    if (typeof document === 'undefined') return undefined;
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Phase 32 KPI-01 + KPI-02 — mount-time analytics seam.
  // Fires ONCE per app mount. Three responsibilities:
  //   1. logEvent('app_open') — Layer 1 analytics (PRODUCT-ANALYTICS.md §2)
  //   2. tend:deviceId — generate via crypto.randomUUID() on first load (D-08)
  //   3. proxy.php?beacon=1&d=<uuid> — anonymous device beacon (KPI-02 / D-07)
  //      Suppressed when dev mode is active (D-10) — mirrors Phase 29 D-04
  //      Sentry suppression. Prevents dev toggling from polluting prod
  //      daily-active-device counts.
  // The fetch is fire-and-forget (.catch silent) — the beacon endpoint MUST
  // NEVER block app boot or surface errors to users.
  useEffect(() => {
    // 1. app_open
    logEvent('app_open');

    // 2. deviceId — bootstrap on first load, opaque, persists until localStorage clear
    let deviceId;
    try {
      deviceId = localStorage.getItem('tend:deviceId');
      if (!deviceId) {
        // crypto.randomUUID is widely supported (Phase 24 raised baseline);
        // fallback to a 16-byte hex composition only if missing (test env safety net).
        deviceId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
          ? crypto.randomUUID()
          : ('fallback-' + Math.random().toString(16).slice(2) + Date.now().toString(16));
        localStorage.setItem('tend:deviceId', deviceId);
      }
    } catch {
      // private mode / quota — beacon will skip below since deviceId stays undefined
    }

    // 3. beacon — D-10: suppress when dev mode is active
    let isDevActive = false;
    try { isDevActive = localStorage.getItem('tend:devmode') === 'true'; } catch {}
    if (!isDevActive && deviceId) {
      // Fire-and-forget. .catch swallows network errors silently — beacon is
      // best-effort; a missing ping is acceptable (the next app open re-fires).
      fetch(`${getApiBase()}/proxy.php?beacon=1&d=${encodeURIComponent(deviceId)}`, { headers: { 'X-Tend-Device-Id': deviceId } }).catch(() => {});
    }
  }, []); // mount-only

  const aiReady = serverKeyReady === true;
  // Phase 34 / RES-01 / D-04 — bundle the 3 setters child components need to
  // invoke the module-level reprobeAiStatus(...) helper from inside their
  // own AI failure paths. Keeps the literal call site `reprobeAiStatus(...)`
  // visible in each child for code review + grep auditability.
  const aiStatusSetters = useMemo(() => ({
    setServerKeyReady,
    setAiStatus,
    setLastAiFailureAt,
  }), []);
  // Convenience wrapper so non-component sites (e.g. inline JSX handlers)
  // can fire reprobeAiStatus without unpacking the bundle each time.
  const reprobeAi = useCallback(() => {
    reprobeAiStatus(setServerKeyReady, setAiStatus, setLastAiFailureAt);
  }, []);
  // Effective features — dev simulate premium overrides all flags to true; simulateUpgradePrompts forces upgrade_prompts on (D-16); simulateReviewPrompt forces review_prompt on (Phase 27 ENG-03)
  const effectiveFeatures = computeEffectiveFeatures(features, devModeActive, simulatePremium, simulateUpgradePrompts, simulateReviewPrompt);
  const effectiveMaxPlants = (devModeActive && simulatePremium) ? 1000 : maxPlants;
  const atPlantCap = plants.length >= effectiveMaxPlants;
  // Write current data version so future updates can detect and merge
  useEffect(()=>{ safeSetItem('tend:dataversion', String(DATA_VERSION), setStorageWarning); },[]);
  // UTM source capture — MO-08
  useEffect(() => {
    try {
      if (localStorage.getItem('tend:referralSource')) return;
      const src = new URLSearchParams(window.location.search).get('utm_source');
      if (src) localStorage.setItem('tend:referralSource', src);
    } catch (_) {}
  }, []);
  useEffect(()=>{ safeSetItem('tend:sortorder', sortOrder, setStorageWarning); },[sortOrder]);

  // Scroll to type group in Plants view after navigating from a type pill
  useEffect(()=>{
    if(!scrollToType||view!=="plants") return;
    requestAnimationFrame(()=>{
      const el=document.getElementById(`type-group-${scrollToType.replace(/\s+/g,"-")}`);
      if(el) el.scrollIntoView({behavior:"smooth",block:"start"});
      setScrollToType(null);
    });
  },[scrollToType,view]);

  // Focus management — move focus to view heading on view switch (WCAG 2.4.3), skip initial mount
  const viewHeadingRef = useRef(null);
  const viewSwitchCount = useRef(0);
  useEffect(()=>{ if(viewSwitchCount.current++>0) viewHeadingRef.current?.focus(); }, [view]);

  // ── Upgrade prompt handlers (ONB-03 / D-15) ────────────────────────────────
  // shownThisSessionRef dedupes per-session so mounting the same banner twice
  // doesn't double-increment the show count (Pitfall 1 — T-22-12).
  const shownThisSessionRef = useRef(new Set());

  // Phase 32 D-01 — fires once per app lifetime when the first plant is added.
  // Initialised true if user already has plants (returning users don't re-fire).
  const firstPlantLoggedRef = useRef(plants.length > 0);

  // Phase 37 Plan 09 (Rule 1 fix) — lifted from inside addPlant() so that
  // the extracted PlantsView (Plan 37-07) can receive it as a prop. Previously
  // the inner function form caused a ReferenceError when App.jsx passed
  // `logFirstPlantIfApplicable={logFirstPlantIfApplicable}` to <PlantsView>
  // since the symbol only existed inside addPlant's closure. PII rule: only
  // `source` is logged; never plant.name or plant.latin.
  const logFirstPlantIfApplicable = useCallback((source) => {
    if (firstPlantLoggedRef.current) return;
    firstPlantLoggedRef.current = true;
    logEvent('first_plant_added', { source });
  }, []);

  const recordPromptShown = useCallback((triggerId) => {
    if (shownThisSessionRef.current.has(triggerId)) return;
    shownThisSessionRef.current.add(triggerId);
    logEvent('upgrade_prompt_shown', { trigger: triggerId });
    setUpgradePromptState(prev => ({
      ...prev,
      [triggerId]: { ...prev[triggerId], count: prev[triggerId].count + 1, lastShownAt: Date.now() }
    }));
  }, []);

  const recordPromptDismissed = useCallback((triggerId) => {
    setUpgradePromptState(prev => ({
      ...prev,
      [triggerId]: { ...prev[triggerId], count: Math.max(prev[triggerId].count, 1), lastShownAt: Date.now(), dismissedAt: Date.now() }
    }));
  }, []);

  const logUpgradePromptClick = useCallback(({ triggerId, showCountAtClick }) => {
    logEvent('upgrade_prompt_tapped', { trigger: triggerId });
    try {
      const raw = localStorage.getItem('tend:upgradePromptClicks');
      const arr = raw ? JSON.parse(raw) : [];
      arr.push({ timestamp: Date.now(), triggerId, showCountAtClick, device: djb2(navigator.userAgent || '') });
      const capped = arr.slice(-500); // mirror affiliate-click cap (D-09)
      safeSetItem('tend:upgradePromptClicks', JSON.stringify(capped), setStorageWarning);
    } catch {}
  }, []);
  // ── End upgrade prompt handlers ────────────────────────────────────────────

  // Phase 27 Plan 03 Gap 2 — Dev-mode OFF→ON simulate reset helper.
  // Resets the relevant prompt state (in-memory + localStorage) and strips the
  // matching card id(s) from tend:dismissedtips so the card appears clean on the
  // next render. Only called when devModeActive is true.
  const resetPromptStateOnDevToggle = useCallback((kind) => {
    if (kind === 'review') {
      const fresh = defaultReviewPromptState();
      setReviewPromptState(fresh);
      setReviewCardClosed(false);
      try { localStorage.setItem('tend:reviewPromptState', JSON.stringify(fresh)); } catch {}
      try {
        const raw = JSON.parse(localStorage.getItem('tend:dismissedtips') || '{}');
        const cleaned = { ...raw, ids: (raw.ids || []).filter(id => id !== 'review-prompt') };
        localStorage.setItem('tend:dismissedtips', JSON.stringify(cleaned));
      } catch {}
    } else if (kind === 'upgrade') {
      const fresh = defaultUpgradePromptState();
      setUpgradePromptState(fresh);
      try { localStorage.setItem('tend:upgradePromptState', JSON.stringify(fresh)); } catch {}
      const upgradeCardIds = ['upgrade-deviceChange', 'upgrade-seasonalNudge', 'upgrade-plantLimit', 'upgrade-sickPlant', 'upgrade-addPlant'];
      try {
        const raw = JSON.parse(localStorage.getItem('tend:dismissedtips') || '{}');
        const cleaned = { ...raw, ids: (raw.ids || []).filter(id => !upgradeCardIds.includes(id)) };
        localStorage.setItem('tend:dismissedtips', JSON.stringify(cleaned));
      } catch {}
    }
  }, []);

  // sickPlant banner visibility state — set true when any note save matches a trouble keyword
  const [sickPlantBannerVisible, setSickPlantBannerVisible] = useState(false);

  // Phase 23 ONB-05 — ephemeral per-plant inline trouble product suggestions (not persisted)
  // Shape: { [plantId]: { product, keyword } }
  const [troubleInlineByPlant, setTroubleInlineByPlant] = useState({});
  // Counter forces HomePanel to re-read tend:troubleNudge after an enqueue or dismiss (mirrors onboardSignal pattern).
  const [troubleNudgeTick, setTroubleNudgeTick] = useState(0);

  // deviceChange + seasonalNudge mount-time triggers (D-12, D-13)
  const [pendingDeviceChange, setPendingDeviceChange] = useState(false);
  const [pendingSeasonalNudge, setPendingSeasonalNudge] = useState(false);

  useEffect(() => {
    // Wait until server status has resolved so features are available
    if (serverKeyReady === null) return;
    // ── Device-change check (D-12 + Pitfall 3 — 48h grace) ──────────────────
    try {
      const currentHash = djb2(navigator.userAgent || '');
      const stored = localStorage.getItem('tend:deviceHash');
      if (!stored) {
        // First-ever visit — write hash and show nothing
        safeSetItem('tend:deviceHash', currentHash, setStorageWarning);
      } else if (stored !== currentHash) {
        const onboardedAt = localStorage.getItem('tend:onboarded');
        const withinGrace = onboardedAt && (Date.now() - new Date(onboardedAt).getTime() < 48*60*60*1000);
        if (!withinGrace) setPendingDeviceChange(true);
        safeSetItem('tend:deviceHash', currentHash, setStorageWarning); // update so next mount no-ops
      }
    } catch {}
    // ── Seasonal-nudge check (D-13 — first mount per season-month per year) ──
    const m = new Date().getMonth() + 1;
    if ([3,6,9,12].includes(m)) {
      const last = upgradePromptState.seasonalNudge.lastShownAt;
      const lastYear = last ? new Date(last).getFullYear() : null;
      const lastMonth = last ? new Date(last).getMonth() + 1 : null;
      const currentYear = new Date().getFullYear();
      if (!last || lastYear !== currentYear || lastMonth !== m) {
        setPendingSeasonalNudge(true);
      }
    }
  }, [serverKeyReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 40 Plan 03 Task 3 — WAL append guard. Every state mutation that
  // persists to localStorage ALSO appends an op to tend:wal when the user is
  // signed-in (session non-null AND tend:supabase-user-id matches), and
  // first-sign-in upload has finished (firstSignInState.active === false).
  // Pre-sign-in mutations live in localStorage only — first-sign-in upload
  // captures them all in one batch via uploadToCloud.
  const maybeWal = useCallback((op, table, cloudId, payload) => {
    try {
      if (!auth.session) return;
      const synced = localStorage.getItem('tend:supabase-user-id');
      if (!synced) return;
      if (auth.firstSignInState?.active) return;
      pushWalOp({ op, table, cloudId, payload });
    } catch {}
  }, [auth.session, auth.firstSignInState?.active]);

  const isDone=useCallback((pId,type,month,year=CURRENT_YEAR)=>!!completions[storageKey(pId,type,month,year)],[completions]);
  const isDismissed=useCallback((pId,type,month,year=CURRENT_YEAR)=>!!completions[dismissKey(pId,type,month,year)],[completions]);
  const toggleDone=useCallback((pId, type, month, year=CURRENT_YEAR)=>{
    const k = storageKey(pId, type, month, year);
    let wasNewMark = false;
    let wasUnmark = false;
    setCompletions(prev => {
      const n = { ...prev };
      if (n[k]) {
        delete n[k];
        wasUnmark = true;
      } else {
        n[k] = true;
        wasNewMark = true;
      }
      return n;
    });
    // Phase 40 Plan 03 — WAL append. Toggle-on inserts a completions row;
    // toggle-off deletes it. Use the storage key as the cloudId so re-toggle
    // converges deterministically.
    if (wasNewMark) maybeWal('insert', 'completions', k, { key: k, plant_id: pId, type, month, year });
    else if (wasUnmark) maybeWal('delete', 'completions', k, {});
    if (wasNewMark) {
      // Phase 32 D-01 — task_completed fires only on genuine NEW marks (not
      // toggle-undo). PII rule: pId NOT logged — task type + month is fine.
      logEvent('task_completed', { taskType: type, month });
      const today = new Date().toISOString().slice(0, 10);
      setCompletionDates(prev2 => {
        if (prev2.has(today)) return prev2;
        const next = new Set([...prev2, today]);
        if (next.size > 60) {
          const sorted = [...next].sort();
          return new Set(sorted.slice(sorted.length - 60));
        }
        return next;
      });
    }
  },[]);
  const toggleDismiss=useCallback((pId,type,month,year=CURRENT_YEAR)=>{
    const k=dismissKey(pId,type,month,year);
    let wasInsert=false;let wasRemove=false;
    setCompletions(prev=>{const n={...prev};if(n[k]){delete n[k];wasRemove=true;}else{n[k]=true;wasInsert=true;}return n;});
    // Phase 40 Plan 03 — WAL append for dismissals (D-13).
    if(wasInsert) maybeWal('insert','dismissals',k,{key:k,plant_id:pId,type,month,year});
    else if(wasRemove) maybeWal('delete','dismissals',k,{});
  },[maybeWal]);
  // Auto-dismiss past months' tasks for a newly added plant
  const dismissPastTasks=useCallback((plant)=>{
    setCompletions(prev=>{
      const n={...prev};
      for(let m=1;m<CURRENT_MONTH;m++){
        for(const t of (plant.tasks[m]||[])){
          if(t.type!=="note") n[dismissKey(plant.id,t.type,m,CURRENT_YEAR)]=true;
        }
      }
      return n;
    });
  },[]);
  // UX-09 follow-up: when the expanded card changes, collapsing the previous card
  // may shift our target card up/down in the viewport. Measure the clicked card's
  // top before setState, then compensate window scroll after the reflow so the
  // thumb-position under the user's finger stays pinned.
  const toggleExpand = id => {
    const cardEl = typeof document !== 'undefined' ? document.querySelector(`[data-plant-id="${id}"]`) : null;
    const beforeTop = cardEl?.getBoundingClientRect().top ?? null;
    setExpandedPlantId(prev => prev === id ? null : id);
    if (cardEl && beforeTop != null) {
      requestAnimationFrame(() => {
        const afterTop = cardEl.getBoundingClientRect().top;
        const diff = afterTop - beforeTop;
        if (Math.abs(diff) > 1) window.scrollBy({ top: diff, behavior: 'instant' });
      });
    }
  };

  // Care/Pottering swipe undo — see src/ui/SwipeableTaskRow.jsx + UndoToast.jsx.
  // Holds the most recent reversible action (one at a time — a fresh swipe
  // replaces an in-flight toast). Toast itself auto-dismisses after 5s.
  const [undoEntry, setUndoEntry] = useState(null);
  const pushUndo = useCallback((label, onUndo) => {
    setUndoEntry({ id: Date.now() + Math.random(), label, onUndo });
  }, []);
  const dismissUndo = useCallback(() => setUndoEntry(null), []);

  const applyAreaFilter=useCallback((list)=>{
    if(filterArea==="all") return list;
    const [aId,sA]=filterArea.split("::");
    return list.filter(p=>!(p.locations||[]).length||(p.locations||[]).some(l=>l.areaId===aId&&(!sA||l.subArea===sA)));
  },[filterArea]);

  const wPlants=useMemo(()=>applyAreaFilter(plants),[plants,filterArea,applyAreaFilter]);
  const monthWorkload=useMemo(()=>MONTHS.map((_,i)=>{ const m=i+1; return wPlants.reduce((a,p)=>a+(p.tasks[m]||[]).filter(t=>t.type!=="note"&&!isDone(p.id,t.type,m)&&!isDismissed(p.id,t.type,m)).length,0); }),[wPlants,isDone,isDismissed]);

  // Phase 27 ENG-03 — engagement data + eligibility (pre-computed for HomePanel prop)
  // Dep array uses primitives (.size / .length) per RESEARCH.md Pitfall 7 — the
  // Set/Array references themselves change on every toggleDone (new Set spread),
  // which would defeat memoisation. Primitive-size deps recompute only when the
  // logical count actually changes.
  const reviewEngagement = useMemo(() => {
    const taskTypes = new Set();
    let taskCompletions = 0;
    for (const k of Object.keys(completions || {})) {
      if (k.startsWith('done:')) {
        taskCompletions++;
        const parts = k.split(':');
        if (parts[2]) taskTypes.add(parts[2]);
      }
    }
    return {
      taskCompletions,
      distinctTaskTypes: taskTypes.size,
      distinctCompletionDays: completionDates.size,
      plantCount: plants.length,
      distinctSessionDays: new Set(sessions).size,
    };
  }, [completions, completionDates.size, plants.length, sessions.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // Phase 27 Plan 03 Gap 2 — dev-mode forcePreview bypasses cap/cooldown/dismiss/mute/engagement.
  // Computed once here and threaded through all shouldShow* call sites.
  const forceReviewPreview  = devModeActive && simulateReviewPrompt;
  const forceUpgradePreview = devModeActive && simulateUpgradePrompts;
  // Session-level reviewCardClosed gate beats forcePreview so dismiss/CTA hides the card
  // immediately; resetPromptStateOnDevToggle('review') clears it so OFF→ON re-shows cleanly.
  const showReviewCard = !reviewCardClosed && shouldShowReviewPrompt(effectiveFeatures, reviewPromptState, reviewEngagement, Date.now(), forceReviewPreview);
  // Phase 32 D-01 — review_prompt_shown fires once per card appearance (ref-deduped
  // per session to match the existing shownThisSessionRef/dedupe pattern).
  const reviewShownLoggedRef = useRef(false);
  if (showReviewCard && !reviewShownLoggedRef.current) {
    reviewShownLoggedRef.current = true;
    logEvent('review_prompt_shown');
  }
  const maxWork=Math.max(...monthWorkload,1);

  const monthPlants=useMemo(()=>wPlants.map(p=>{
    let tasks=p.tasks[selectedMonth]||[];
    // Defensive: normalise bare "plant" type to "plant/sow" so the filter pill catches both (BUG-01)
    if(filterTypes.size>0) tasks=tasks.filter(t=>filterTypes.has(t.type==="plant"?"plant/sow":t.type));
    if(!showDone) tasks=tasks.filter(t=>t.type!=="note"&&!isDone(p.id,t.type,selectedMonth)&&!isDismissed(p.id,t.type,selectedMonth));
    tasks=tasks.filter(t=>t.type==="note"||!isDismissed(p.id,t.type,selectedMonth));
    return{...p,tasks};
  }).filter(p=>p.tasks.length>0),[wPlants,selectedMonth,filterTypes,showDone,isDone,isDismissed]);

  const outstandingCount=useMemo(()=>wPlants.reduce((acc,p)=>acc+(p.tasks[selectedMonth]||[]).filter(t=>t.type!=="note"&&!isDone(p.id,t.type,selectedMonth)&&!isDismissed(p.id,t.type,selectedMonth)).length,0),[wPlants,selectedMonth,isDone,isDismissed]);
  const activityBadgeCount=useMemo(()=>{const types=new Set();wPlants.forEach(p=>{(p.tasks[CURRENT_MONTH]||[]).forEach(t=>{if(t.type!=="note"&&!isDone(p.id,t.type,CURRENT_MONTH)&&!isDismissed(p.id,t.type,CURRENT_MONTH))types.add(t.type);});});return types.size;},[wPlants,isDone,isDismissed]);

  const addPlant=(scheduleOverride, colourOverride={}, meta={})=>{
    if(!newPlant.name.trim()) return null;
    if(!newPlant.type) return null; // UAT gap 7 defensive guard — no type = no add
    if(atPlantCap){setShowCapNotice(true);return null;}
    const tasks = scheduleOverride || richDefaultTasks(newPlant.type, newPlant.name, newPlant.latin);
    const h=parseFloat(newPlant.height); const sp=parseFloat(newPlant.spread);
    const newId=nextPlantId(plants);
    // Phase 34 / RES-01 / D-03 — provenance for the expanded-card "we used a default
    // schedule" note. Set only when the AI lookup was attempted and failed
    // (meta.usedFallback === true) — NOT for free-tier users who never had AI access.
    const provenance = meta && meta.usedFallback ? { addedVia: 'fallback-default' } : {};
    // ENG-01 — enrich with sun/soil/hardiness from PLANT_SUGGESTIONS (D-02 hybrid persistence)
    const p=enrichGrowingConditions({id:newId,name:newPlant.name.trim(),variety:newPlant.variety.trim()||undefined,latin:newPlant.latin.trim(),type:newPlant.type,count:parseInt(newPlant.count)||1,wiki:newPlant.wiki.trim()||newPlant.latin.trim(),locations:[{areaId:newPlant.area||areas[0]?.id,subArea:newPlant.subArea||""}],tasks,colour:colourOverride||{},...(!isNaN(h)&&h>0?{height:h}:{}),...(!isNaN(sp)&&sp>0?{spread:sp}:{}),...provenance});
    // Static colour fallback for free-tier users (D-16)
    if(Object.keys(p.colour||{}).length===0){
      const nameKey=p.name.toLowerCase();
      const varKey=p.variety?(nameKey+" "+p.variety.toLowerCase()).trim():null;
      const staticCol=STATIC_PLANT_COLOURS[varKey]||STATIC_PLANT_COLOURS[nameKey];
      if(staticCol) p.colour=staticCol;
    }
    // Phase 32 D-01 — first_plant_added on the leading edge of plant insertion.
    // logFirstPlantIfApplicable is hoisted to App-level scope (see above) so
    // the extracted PlantsView can receive it as a prop (Plan 37-09 Rule 1 fix).
    logFirstPlantIfApplicable('form');
    setPlants(prev=>[...prev,p]);
    dismissPastTasks(p);
    setSortOrder("recently-added");
    setNewPlant(prev=>({...prev,name:"",variety:"",latin:"",wiki:""}));
    setShowAddForm(false);
    requestAnimationFrame(()=>window.scrollTo({top:0,behavior:"smooth"}));
    // Phase 40 Plan 03 — WAL append for plant insert. Generate a fresh
    // cloudId for the new plant; the Worker /sync handler accepts a UUID
    // it has not seen before and inserts under the JWT-derived garden_id.
    const cloudId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `plant-${Date.now()}-${Math.random()}`;
    p.cloudId = cloudId;
    maybeWal('insert', 'plants', cloudId, {
      local_id: p.id, name: p.name, variety: p.variety, latin: p.latin,
      type: p.type, count: p.count, wiki: p.wiki,
      height: p.height, spread: p.spread,
      locations: p.locations, tasks: p.tasks, colour: p.colour,
    });
    return newId;
  };
  const updatePlant=(id,updates)=>{
    setPlants(prev=>prev.map(p=>p.id===id?{...p,...updates}:p));
    // Phase 40 Plan 03 — WAL append for plant update. Resolve cloudId from
    // current plant; if absent (pre-sync record) skip — the next first-sign-in
    // upload covers it.
    const target = plants.find(p=>p.id===id);
    if (target?.cloudId) maybeWal('update', 'plants', target.cloudId, updates);
  };
  const removePlant=id=>{
    const plant=plants.find(p=>p.id===id);
    setConfirmModal({
      title:"Remove plant",
      message:`Remove ${plant?.name||"this plant"} from Tend? This cannot be undone.`,
      confirmLabel:"Remove plant",
      onConfirm:()=>{
        setPlants(prev=>prev.filter(p=>p.id!==id));
        setConfirmModal(null);
        // Phase 40 Plan 03 — WAL append for plant delete.
        if (plant?.cloudId) maybeWal('delete', 'plants', plant.cloudId, {});
      }
    });
  };

  // Phase 34 IMG-01 / D-07 — "Report wrong image" flow. ConfirmModal → POST →
  // toast. On success, append to tend:reportedImages so the same plant's link
  // is suppressed for 30 days. Failures show a transient error toast; never
  // blocks the user (the resolver still has the override pipeline as the long-term
  // fix path).
  const handleReportImage=(plant)=>{
    if(!plant) return;
    setConfirmModal({
      title:"This image doesn't look right?",
      message:`Report the image we're showing for ${plant.name}? Our team will check it.`,
      confirmLabel:"Report image",
      variant:"neutral",
      onConfirm:async()=>{
        setConfirmModal(null);
        const cacheKey=plant.latin||plant.wiki||"";
        const currentImageUrl=imgCache[cacheKey]||"";
        try{
          const res=await fetch("image-report.php",{
            method:"POST",
            headers:{"Content-Type":"application/json"},
            body:JSON.stringify({
              plantId:String(plant.id),
              name:plant.name||"",
              latin:plant.latin||"",
              wiki:plant.wiki||"",
              imageUrl:currentImageUrl,
              appVersion:(typeof __GIT_HASH__!=="undefined")?__GIT_HASH__:"dev",
              region:(localStorage.getItem("tend:region")||"unknown"),
            }),
          });
          const data=await res.json().catch(()=>({}));
          if(res.ok&&data&&data.ok){
            setReportedImages(prev=>[
              ...prev.filter(e=>e.plantId!==String(plant.id)),
              {plantId:String(plant.id),wiki:plant.wiki||"",reportedAt:Date.now()},
            ]);
            setReportToast("success");
            setTimeout(()=>setReportToast(null),4500);
          } else {
            setReportToast("error");
            setTimeout(()=>setReportToast(null),4500);
          }
        } catch {
          setReportToast("error");
          setTimeout(()=>setReportToast(null),4500);
        }
      },
      onCancel:()=>setConfirmModal(null),
    });
  };

  const filteredPlants=useMemo(()=>wPlants.filter(p=>
    !searchQuery||p.name.toLowerCase().includes(searchQuery.toLowerCase())||(p.variety&&p.variety.toLowerCase().includes(searchQuery.toLowerCase()))||(p.latin&&p.latin.toLowerCase().includes(searchQuery.toLowerCase()))
  ),[wPlants,searchQuery]);

  const sortedPlants=useMemo(()=>{
    const s=[...filteredPlants];
    const byName=(a,b)=>a.name.localeCompare(b.name);
    switch(sortOrder){
      case "name-za": return s.sort((a,b)=>b.name.localeCompare(a.name));
      case "type":    return s.sort((a,b)=>a.type.localeCompare(b.type)||byName(a,b));
      case "area": {
        const aMap=Object.fromEntries(areas.map(a=>[a.id,a.name]));
        const locLabel=p=>{ const l=p.locations?.[0]; return l?`${aMap[l.areaId]||l.areaId}${l.subArea?" › "+l.subArea:""}` : ""; };
        return s.sort((a,b)=>locLabel(a).localeCompare(locLabel(b))||byName(a,b));
      }
      case "tasks-month": return s.sort((a,b)=>{
        const n=p=>(p.tasks[CURRENT_MONTH]||[]).filter(t=>t.type!=="note"&&!isDone(p.id,t.type,CURRENT_MONTH)).length;
        return n(b)-n(a)||byName(a,b);
      });
      case "recently-added": return s.sort((a,b)=>b.id-a.id);
      default: return s.sort(byName); // name-az
    }
  },[filteredPlants,sortOrder,areas,isDone]);

  const areaFilterOptions=[{val:"all",label:"All spaces"},...areas.flatMap(a=>[{val:a.id,label:a.name},...(a.subAreas||[]).map(s=>({val:`${a.id}::${s}`,label:`  ↳ ${s}`}))])];

  const tv = buildThemeVars(darkMode);
  return (
    <div className="app-shell" style={{fontFamily:"var(--font-family-body)",background:"var(--c-page)",color:"var(--c-text)",...tv}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Lora:wght@400;500;600&display=swap');
        *{box-sizing:border-box;}body{margin:0;}
        /* PR #306 — viewport sizing. .app-shell min-height:100dvh (vh fallback)
           so the shell tracks the visual viewport. Body scrolls naturally when
           content exceeds 100dvh; URL-bar collapse-on-scroll works as expected.
           desktop-sidebar uses dvh too. bottom-nav stays position:fixed (the
           Twitter/Instagram pattern) with bottom:var(--vv-bottom-offset,0) so
           the JS hook can correct iOS 17 PWA / iOS 26 keyboard quirks.
           PR #307 — explicit cascade default + background-clip lock. */
        :root { --vv-bottom-offset: 0px; }
        .bottom-nav { background-clip: border-box !important; }
        .app-shell { min-height: 100vh; min-height: 100dvh; }
        /* PR #309 — comprehensive footer pin: every view rendered inside
           .main-content inherits viewport-fill behaviour. Body always
           overflows by ~app-header + main-content padding-bottom (~140px) →
           iOS Safari enters scrollable mode → URL bar auto-collapses on first
           scroll → fixed bottom-nav anchored to visual viewport bottom.
           Without this, short-content views (Year, Pottering, or any view
           with 0 plants) had body == 100dvh exactly, leaving the document
           non-scrollable and iOS anchoring the nav to the layout viewport
           bottom (always large) — pushing the nav below the visible
           URL-bar-shrunken area. PR #308 patched Year+Pottering individually;
           this rule covers all current and future views, including the
           0-plants scenario for Plants/Supplies/Colours. */
        .main-content { min-height: 100vh; min-height: 100dvh; }
        .desktop-sidebar { height: calc(100vh - env(safe-area-inset-top, 0px)); height: calc(100dvh - env(safe-area-inset-top, 0px)); }
        button{cursor:pointer;font-family:'Lora',serif;}
        input,select,textarea{font-family:'Lora',serif;}
        .hover-lift:hover{transform:translateY(-2px);box-shadow:0 4px 14px rgba(0,0,0,0.12)!important;}
        .task-row:hover{background:var(--c-blight)!important;}
        .card:hover{box-shadow:0 4px 18px rgba(0,0,0,0.10)!important;}
        .nav-btn:hover{background:#3d6b3d!important;color:#fff!important;}
        .stype-btn:hover{border-color:var(--c-heading)!important;background:var(--c-tint)!important;transform:translateY(-1px);}
        .main-content input,.main-content select,.main-content textarea{border-color:var(--c-border)!important;background:var(--c-bg)!important;color:var(--c-text)!important;}
        .main-content input::placeholder,.main-content textarea::placeholder{color:var(--c-text-4)!important;}
        .session-row{transition:background 0.2s;}
        .session-row:hover{background:rgba(0,0,0,0.025)!important;}
        .colour-cell:hover{transform:scale(1.1);z-index:10;box-shadow:0 2px 8px rgba(0,0,0,0.25);}
        .inv-row:hover{background:var(--c-blight)!important;}
        @media (max-width:700px){
          .bottom-nav{display:flex!important;}
          .main-content{padding-bottom:calc(72px + env(safe-area-inset-bottom, 8px))!important;}
        }
        @media (min-width:701px){
          .desktop-sidebar{display:flex!important;}
          .main-content{margin-left:200px!important;max-width:none!important;padding:20px 28px!important;}
          .app-header{margin-left:200px!important;padding-top:14px!important;padding-bottom:14px!important;}
          .app-header>div>div:first-child{display:none!important;}
          .app-header>div{justify-content:flex-end!important;}
        }
        .bottom-nav{padding-bottom:env(safe-area-inset-bottom, 5px)!important;}
        @media print {
          .no-print{display:none!important;}
          div[style*="background:#3a6b3a"]{display:none!important;}
          div[style*="background:#2d5a2d"]{display:none!important;}
        }
        :focus-visible{outline:2px solid ${darkMode?"#7ecb7e":"#2d5a2d"}!important;outline-offset:2px!important;}
        :focus:not(:focus-visible){outline:none;}
        /* #226 — view-heading is tabIndex=-1 + programmatically focused on view switch
           so screen readers announce the new view. It is not a keyboard target, so
           the focus-visible outline must not render on it (was producing inconsistent
           "boxes around titles" depending on prior interaction modality). */
        h2[tabindex="-1"]:focus,h2[tabindex="-1"]:focus-visible{outline:none!important;box-shadow:none!important;}
        @keyframes task-pulse {
          0%   { box-shadow: 0 0 0 0px var(--c-heading); }
          50%  { box-shadow: 0 0 0 6px rgba(45, 90, 45, 0.35); }
          100% { box-shadow: 0 0 0 0px transparent; }
        }
        .task-pulse { animation: task-pulse 600ms ease-out 3; border-radius: 12px; }
        @media (prefers-reduced-motion: reduce) {
          .task-pulse { animation: none; }
        }
        @keyframes tendUpgradeBannerIn {
          from { opacity:0; transform:translateY(-4px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="tendUpgradeBannerIn"] { animation:none!important; }
        }
        /* Onboarding final-card sunflower: stem + leaves + disc visible from
           the start; all 12 petals burst out of the disc centre together.
           Transform-origin pinned to the disc via view-box coords so the
           scale animation genuinely emerges from the flower centre. */
        @keyframes onb-sun-petal-burst {
          0%   { opacity: 0; transform: scale(0); }
          25%  { opacity: 1; }
          100% { opacity: 1; transform: scale(1); }
        }
        .onb-sun-petal {
          transform-origin: 90px 85px;
          transform-box: view-box;
          opacity: 0;
          /* 0.5s delay + 3s duration = petals finish opening at 3.5s */
          animation: onb-sun-petal-burst 3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .onb-sun-petal { opacity: 1 !important; transform: none !important; animation: none !important; }
        }
        /* Month-view empty state — bees on lavender. Stems and flower
           spikes are drawn static; flower heads sway gently on their own
           rhythms; two bees loop through the lavender, visiting each spike. */
        .mo-lav-flower { transform-box: view-box; }
        .mo-lav-flower-1 {
          transform-origin: 80px 87px;
          animation: mo-lav-sway-a 4s ease-in-out infinite, mo-lav-nudge-1 9s ease-in-out 1.5s infinite;
        }
        .mo-lav-flower-2 {
          transform-origin: 150px 77px;
          animation: mo-lav-sway-b 4.5s ease-in-out infinite, mo-lav-nudge-2 9s ease-in-out 1.5s infinite;
        }
        .mo-lav-flower-3 {
          transform-origin: 220px 82px;
          animation: mo-lav-sway-a 4.2s ease-in-out infinite, mo-lav-nudge-3 9s ease-in-out 1.5s infinite;
        }
        @keyframes mo-lav-sway-a {
          0%, 100% { transform: rotate(-2deg); }
          50% { transform: rotate(2deg); }
        }
        @keyframes mo-lav-sway-b {
          0%, 100% { transform: rotate(2deg); }
          50% { transform: rotate(-2deg); }
        }
        @keyframes mo-lav-nudge-1 {
          0%, 22%, 42%, 100% { }
          26% { transform: rotate(-5deg); }
          32% { transform: rotate(3deg); }
        }
        @keyframes mo-lav-nudge-2 {
          0%, 40%, 100% { }
          50% { transform: rotate(-5deg); }
          56% { transform: rotate(3deg); }
        }
        @keyframes mo-lav-nudge-3 {
          0%, 70%, 100% { }
          75% { transform: rotate(-5deg); }
          81% { transform: rotate(3deg); }
        }
        .mo-bee { transform-box: view-box; }
        .mo-bee-1 {
          transform-origin: 80px 87px;
          animation: mo-bee-path-1 9s ease-in-out 1.5s infinite;
        }
        .mo-bee-2 {
          transform-origin: 220px 82px;
          animation: mo-bee-path-2 10s ease-in-out 3.5s infinite;
        }
        @keyframes mo-bee-path-1 {
          0%   { opacity: 0; transform: translate(-60px, 40px); }
          8%   { opacity: 1; }
          25%  { opacity: 1; transform: translate(0, 0); }
          35%  { opacity: 1; transform: translate(10px, 6px); }
          50%  { opacity: 1; transform: translate(70px, -10px); }
          60%  { opacity: 1; transform: translate(70px, -6px); }
          75%  { opacity: 1; transform: translate(140px, -5px); }
          85%  { opacity: 1; transform: translate(140px, -2px); }
          95%  { opacity: 1; transform: translate(160px, 20px); }
          100% { opacity: 0; transform: translate(180px, 40px); }
        }
        @keyframes mo-bee-path-2 {
          0%   { opacity: 0; transform: translate(60px, 50px); }
          8%   { opacity: 1; }
          30%  { opacity: 1; transform: translate(0, 0); }
          42%  { opacity: 1; transform: translate(-70px, -5px); }
          55%  { opacity: 1; transform: translate(-70px, 0); }
          68%  { opacity: 1; transform: translate(-140px, 5px); }
          80%  { opacity: 1; transform: translate(-140px, 8px); }
          95%  { opacity: 1; transform: translate(-170px, 30px); }
          100% { opacity: 0; transform: translate(-190px, 50px); }
        }
        .mo-bee-wing {
          transform-origin: center;
          transform-box: fill-box;
          animation: mo-bee-wing-flicker 0.15s linear infinite;
        }
        @keyframes mo-bee-wing-flicker {
          0%, 100% { opacity: 0.75; transform: scaleY(1); }
          50%      { opacity: 0.35; transform: scaleY(0.4); }
        }
        @media (prefers-reduced-motion: reduce) {
          .mo-lav-flower-1,
          .mo-lav-flower-2,
          .mo-lav-flower-3,
          .mo-bee-1,
          .mo-bee-2,
          .mo-bee-wing {
            animation: none !important;
            transform: none !important;
          }
          .mo-bee-1, .mo-bee-2 { opacity: 0 !important; }
        }
      `}</style>

      {/* Offline indicator — shown when there is no network connection */}
      {!isOnline&&(
        <div className="no-print" style={{background:"var(--c-overdue-text)",color:"var(--c-overdue-bg)",fontSize:'0.8125rem',fontWeight:600,textAlign:"center",padding:"7px 16px",letterSpacing:"0.01em"}}>
          You're offline — plant data and task tracking still work; AI features and plant images need a connection
        </div>
      )}

      {/* Phase 34 / RES-01 / D-02 — AI availability banners.
          Stacking order top->bottom: offline > AI down > AI degraded > update-available.
          Both banners use Phase 33 design tokens (D-17) — no ad-hoc hex.
          'no-print' so view==='print' is unaffected. */}
      {aiStatus === 'down' && (
        <div className="no-print" role="status" aria-live="polite" style={{
          background: "var(--c-danger)", color: "#fff",
          fontSize: '0.8125rem', fontWeight: 600, textAlign: "center",
          padding: "7px 16px", letterSpacing: "0.01em"
        }}>
          AI features temporarily unavailable — your plants, tasks and reminders still work
        </div>
      )}
      {aiStatus === 'degraded' && (
        <div className="no-print" role="status" aria-live="polite" style={{
          background: "var(--c-tint)", color: "var(--c-heading)",
          fontSize: '0.8125rem', fontWeight: 600, textAlign: "center",
          padding: "7px 16px", letterSpacing: "0.01em",
          borderBottom: "1px solid var(--c-border)"
        }}>
          AI features may be slow — try again shortly
        </div>
      )}

      {/* Update available banner — shown when a new service worker is waiting.
          Banner sits at the very top of the viewport (above the sticky app-header),
          so its top padding must respect Dynamic Island / status-bar safe-area or
          the message disappears under the inset. */}
      {/* PR #295 — auto-update indicator. Renders only when the splash-mount
          auto-update path is in flight AND the operation has taken longer
          than ~500ms (most updates feel instant; we only surface the
          indicator if there's a real wait). */}
      {updating && (
        <div role="status" aria-live="polite" className="no-print"
          data-pwa-updating="true"
          style={{position:"fixed",inset:0,background:"var(--c-page)",zIndex:9999,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,fontFamily:"var(--font-family-display)"}}>
          <div style={{width:32,height:32,border:"3px solid var(--c-border)",borderTopColor:"var(--c-brand-deep)",borderRadius:"50%",animation:"tendSpin 800ms linear infinite"}}/>
          <div style={{fontSize:'var(--font-h3)',color:"var(--c-heading)"}}>Updating Tend…</div>
          <style>{`@keyframes tendSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {needRefresh&&!updating&&(
        <div className="no-print" style={{background:"var(--c-success)",color:"#fff",fontSize:'0.8125rem',fontWeight:600,textAlign:"center",padding:"calc(7px + env(safe-area-inset-top, 0px)) 16px 7px",letterSpacing:"0.01em",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}} onClick={()=>updateServiceWorker(true)}>
          <Download size={14}/> New version available — tap to update
        </div>
      )}

      {/* Header */}
      <div className="app-header no-print" style={{position:"sticky",top:0,zIndex:180,background:"var(--c-nav-bg)",color:"var(--c-nav-text)",padding:`calc(9px + env(safe-area-inset-top, 0px)) 20px 7px`,boxShadow:"0 2px 12px rgba(0,0,0,0.18)"}}>
        <div className="row-between">
          <div>
            <h1 onClick={()=>setView("year")} style={{margin:0,fontFamily:"var(--font-family-display)",fontSize:"var(--font-h1)",fontWeight:700,letterSpacing:"0.03em",cursor:"pointer",display:"flex",alignItems:"center",gap:8}}><Sprout size={24} color="var(--c-nav-text)" strokeWidth={2}/>Tend</h1>
            <p style={{margin:"2px 0 0",fontSize:'0.8125rem',opacity:0.9,fontStyle:"italic"}}>Your garden companion</p>
          </div>
          <div style={{display:"flex",gap:8,position:"relative",alignItems:"center"}}>
            {/* Ask Tend is always visible on all tiers — per D-15 */}
            <button onClick={()=>setView("ask")} title="Ask Tend"
              style={{background:view==="ask"?"rgba(255,255,255,0.3)":"rgba(255,255,255,0.15)",color:"var(--c-nav-text)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:20,padding:"6px 10px",lineHeight:1,display:"flex",alignItems:"center",gap:5,fontSize:'0.8125rem',fontWeight:view==="ask"?700:400}}>
              <MessageCircle size={16} color="var(--c-nav-text)"/><span style={{fontFamily:"var(--font-family-body)"}}>Ask Tend</span>
            </button>
            <button onClick={()=>setShowMenu(v=>!v)} style={{background:showMenu?"rgba(255,255,255,0.3)":"rgba(255,255,255,0.15)",color:"var(--c-nav-text)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:20,padding:"6px 10px",lineHeight:1,display:"flex",alignItems:"center"}} aria-label="Open menu" title="Menu"><Menu size={18} color="var(--c-nav-text)"/></button>
            {showMenu&&(
              <>
                <div style={{position:"fixed",inset:0,zIndex:99}} onClick={()=>setShowMenu(false)}/>
                <div style={{position:"absolute",top:"calc(100% + 8px)",right:0,zIndex:100,background:"var(--c-bg)",borderRadius:12,boxShadow:"0 4px 24px rgba(0,0,0,0.18)",minWidth:210,overflow:"hidden"}}>
                  {/* Phase 40 Plan 02 — Sign in / out menu item.
                      Unauthed → opens SignInModal. Authed → shows user email + sign out.
                      Plan 04 will extend this with Account / Delete account. */}
                  {!auth.session ? (
                    <button onClick={()=>{setShowSignInModal(true);setShowMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"13px 18px",background:"none",border:"none",borderBottom:"1px solid var(--c-blight)",fontSize:'0.875rem',color:"var(--c-heading)",cursor:"pointer",fontWeight:600,textAlign:"left"}}>
                      <LogIn size={16}/><span>Sign in to sync</span>
                    </button>
                  ) : (
                    <>
                      <div style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"13px 18px",borderBottom:"1px solid var(--c-blight)",fontSize:'0.8125rem',color:"var(--c-heading-soft)",fontWeight:500,textAlign:"left",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        <UserCircle2 size={16}/><span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{auth.user?.email || 'Signed in'}</span>
                      </div>
                      <button onClick={()=>{auth.signOut();setShowMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"13px 18px",background:"none",border:"none",borderBottom:"1px solid var(--c-blight)",fontSize:'0.875rem',color:"var(--c-heading)",cursor:"pointer",fontWeight:600,textAlign:"left"}}>
                        <LogOut size={16}/><span>Sign out</span>
                      </button>
                    </>
                  )}
                  {auth.user&&isAdminEmail(auth.user.email)&&(
                    <button onClick={()=>{setView("admin");setShowMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"13px 18px",background:"none",border:"none",borderBottom:"1px solid var(--c-blight)",fontSize:'0.875rem',color:"var(--c-heading)",cursor:"pointer",fontWeight:600,textAlign:"left"}}>
                      <Shield size={16}/><span>Admin</span>
                    </button>
                  )}
                  <button onClick={()=>{setShowSettings(true);setShowMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"13px 18px",background:!aiReady&&serverKeyReady!==null?"#fff8f8":"none",border:"none",borderBottom:"1px solid var(--c-blight)",fontSize:'0.875rem',color:!aiReady&&serverKeyReady!==null?"var(--c-danger)":"var(--c-heading)",cursor:"pointer",fontWeight:600,textAlign:"left"}}>
                    <Settings size={16}/><span>Settings{!aiReady&&serverKeyReady!==null&&" — add API key"}</span>
                  </button>
                  {/* Phase 31.1 Plan 02 (#207): Manage Spaces moved to Settings > Garden Setup */}
                  <button onClick={()=>{setView("print");setShowMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"13px 18px",background:"none",border:"none",borderBottom:"1px solid var(--c-blight)",fontSize:'0.875rem',color:"var(--c-heading)",cursor:"pointer",fontWeight:600,textAlign:"left"}}>
                    <Printer size={16}/><span>Print task sheet</span>
                  </button>
                  <button onClick={()=>{exportCSV(plants,areas);setShowMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"13px 18px",background:"none",border:"none",borderBottom:"1px solid var(--c-blight)",fontSize:'0.875rem',color:"var(--c-heading)",cursor:"pointer",fontWeight:600,textAlign:"left"}}>
                    <Download size={16}/><span>Export Plant Database</span>
                  </button>
                  <button onClick={()=>{setShowShare(true);setShowMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"13px 18px",background:"none",border:"none",borderBottom:"1px solid var(--c-blight)",fontSize:'0.875rem',color:"var(--c-heading)",cursor:"pointer",fontWeight:600,textAlign:"left"}}>
                    <Share2 size={16}/><span>Recommend Tend</span>
                  </button>
                  <button onClick={()=>{setShowFeedback(true);setShowMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"13px 18px",background:"none",border:"none",borderBottom:"1px solid var(--c-blight)",fontSize:'0.875rem',color:"var(--c-heading)",cursor:"pointer",fontWeight:600,textAlign:"left"}}>
                    <MessageSquare size={16}/><span>Send feedback</span>
                  </button>
                  <button onClick={()=>{setShowAbout(true);setShowMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"13px 18px",background:"none",border:"none",fontSize:'0.875rem',color:"var(--c-heading)",cursor:"pointer",fontWeight:600,textAlign:"left"}}>
                    <Info size={16}/><span>About Tend</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Nav — hidden on mobile, replaced by bottom tab bar */}
      {(()=>{
        const NAV_ITEMS=[
          {key:"year",   icon:Calendar,       label:"Year",            short:"Year"},
          {key:"month",  icon:CalendarDays,   label:"Month",           short:"Month"},
          {key:"session",icon:Shovel,         label:potteringLabel,    short:potteringLabel, badge:activityBadgeCount>0?activityBadgeCount:null},
          {key:"shed",   icon:Package,        label:"Supplies",        short:"Supplies"},
          {key:"palette",icon:Palette,        label:"Colours",         short:"Colours"},
          {key:"plants", icon:Leaf,           label:"Plants",          short:"Plants"},
        ];
        const navBtn=(key,NavIcon,label,badge,extraStyle={})=>(
          <button key={key} className="nav-btn" onClick={()=>{setView(key);if(key==="session")setSessionTaskType(null);}}
            style={{background:view===key?"var(--c-blight)":"transparent",color:view===key?"var(--c-heading)":"#d9f0d9",border:"none",borderRadius:20,padding:"6px 12px",fontSize:'0.8125rem',fontWeight:view===key?600:400,display:"flex",alignItems:"center",gap:5,position:"relative",whiteSpace:"nowrap",...extraStyle}}>
            <NavIcon size={15}/>
            {label}
            {badge&&<span style={{background:"var(--c-danger)",color:"#fff",borderRadius:10,padding:"1px 6px",fontSize:'0.6875rem',fontWeight:700,marginLeft:2}}>{badge}</span>}
          </button>
        );
        return(<>
          {/* desktop-sidebar — height tracked via .desktop-sidebar CSS rule (dvh) per PR #306 */}
          <aside className="desktop-sidebar no-print" style={{position:"fixed",top:"env(safe-area-inset-top, 0px)",left:0,width:200,background:"var(--c-nav-bg)",display:"none",flexDirection:"column",overflowY:"auto",zIndex:150,borderRight:"1px solid rgba(255,255,255,0.1)"}}>
            <div style={{padding:"22px 16px 14px",borderBottom:"1px solid rgba(255,255,255,0.15)",cursor:"pointer",display:"flex",alignItems:"center",gap:8}} onClick={()=>setView("year")}>
              <Sprout size={20} color="var(--c-nav-text)" strokeWidth={2}/>
              <span style={{fontFamily:"var(--font-family-display)",fontSize:'1.1875rem',fontWeight:700,color:"var(--c-nav-text)"}}>Tend</span>
            </div>
            <nav role="navigation" aria-label="Main navigation" style={{flex:1,padding:"8px 0",overflowY:"auto"}}>
              {NAV_ITEMS.map(({key,icon:NavIcon,label,badge})=>(
                <button key={key} onClick={()=>{setView(key);if(key==="session")setSessionTaskType(null);}}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 16px",background:view===key?"rgba(255,255,255,0.15)":"none",border:"none",color:view===key?"#fff":"#a8cca8",fontSize:'0.875rem',fontWeight:view===key?600:400,fontFamily:"var(--font-family-body)",cursor:"pointer",textAlign:"left",position:"relative"}}>
                  <NavIcon size={16}/>{label}
                  {badge&&<span style={{marginLeft:"auto",background:"var(--c-danger)",color:"#fff",borderRadius:10,padding:"1px 6px",fontSize:'0.6875rem',fontWeight:700}}>{badge}</span>}
                </button>
              ))}
            </nav>
          </aside>
          {/* Bottom tab bar — mobile only. Visible in admin too so the user can navigate
              back to other views without having to tap the admin Back button first
              (admin batch 1, item 3 — footer parity with main app, Plan 40.1.02).
              bottom uses --vv-bottom-offset (visualViewport JS hook) so iOS Safari
              URL-bar transitions and iOS keyboard don't unpin the nav (Plan 40.1.07 / PR #306). */}
          {view!=="print"&&(
            <div role="navigation" aria-label="Main navigation" className="bottom-nav no-print" style={{position:"fixed",bottom:"var(--vv-bottom-offset, 0px)",left:0,right:0,background:"var(--c-nav-bg)",borderTop:"1px solid rgba(255,255,255,0.15)",zIndex:200,display:"none",paddingBottom:"env(safe-area-inset-bottom)"}}>

              {NAV_ITEMS.map(({key,icon:NavIcon,short,badge})=>(
                <button key={key} onClick={()=>{setView(key);if(key==="session")setSessionTaskType(null);}}
                  style={{flex:1,background:"none",border:"none",color:view===key?"#fff":"#88b888",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,padding:"10px 2px 8px",fontSize:'0.625rem',fontFamily:"var(--font-family-body)",cursor:"pointer",position:"relative"}}>
                  <NavIcon size={19}/>
                  <span style={{fontWeight:view===key?600:400}}>{short}</span>
                  {badge&&<span style={{position:"absolute",top:4,right:"calc(50% - 16px)",background:"var(--c-danger)",color:"#fff",borderRadius:8,padding:"0 4px",fontSize:'0.5625rem',fontWeight:700,lineHeight:"14px"}}>{badge}</span>}
                </button>
              ))}
            </div>
          )}
        </>);
      })()}

      <div role="main" className="main-content" style={{padding:"18px 14px"}}>
        {view!=="print"&&<ViewHeader view={view} filterArea={filterArea} setFilterArea={setFilterArea} areaFilterOptions={areaFilterOptions} headingRef={viewHeadingRef} selectedMonth={selectedMonth} potteringLabel={potteringLabel} resolvedRegion={resolvedRegion}/>}

        {/* Phase 38 D-15 — Suspense boundary for lazy view chunks */}
        <Suspense fallback={<div style={{padding:'2rem',textAlign:'center',fontFamily:"var(--font-family-body)",color:'var(--c-text-2)'}}>Loading…</div>}>

        {/* Year — extracted to src/views/year/YearView.jsx (Plan 37-07 continuation) */}
        {view==="year"&&<YearView
          monthWorkload={monthWorkload} maxWork={maxWork} darkMode={darkMode}
          setSelectedMonth={setSelectedMonth} setView={setView}
          plants={wPlants}
          homePanelProps={{
            plants: wPlants, inventory, isDone, isDismissed, setView, setSortOrder, setScrollToType,
            onShare: ()=>setShowAchievement(true),
            showOnboardNudge,
            onResumeOnboarding: handleResumeOnboarding,
            onDismissOnboardNudge: handleDismissOnboardNudge,
            allPlants: plants,
            areas,
            filterArea,
            resolvedRegion,
            onAddInspire: (sugg)=>{
              // ONB-04 D-08 — pre-fill AddPlantForm with the Inspire suggestion, then open it.
              // Zero-AI: AddPlantForm uses richDefaultTasks when AI schedule lookup isn't available.
              setView("plants");
              setNewPlant(prev=>({
                ...prev,
                name: sugg.name || "",
                latin: sugg.latin || "",
                type: sugg.type || prev.type || "shrub",
                wiki: sugg.wiki || sugg.latin || "",
                variety: "",
              }));
              setShowAddForm(true);
            },
            pendingDeviceChange,
            pendingSeasonalNudge,
            upgradeProps: {
              effectiveFeatures,
              featureTier,
              upgradePromptState,
              shouldShow:(tid)=>shouldShowUpgrade(tid, effectiveFeatures, featureTier, upgradePromptState, undefined, forceUpgradePreview),
              onShow:(tid)=>recordPromptShown(tid),
              onDismiss:(tid)=>recordPromptDismissed(tid),
              onPrimary:(tid)=>{ logUpgradePromptClick({triggerId:tid, showCountAtClick: upgradePromptState[tid]?.count||0}); window.dispatchEvent(new CustomEvent('tend:premium-source',{detail:tid})); setShowPremium(true); },
            },
            onDismissDeviceChange: ()=>{ recordPromptDismissed('deviceChange'); setPendingDeviceChange(false); },
            onDismissSeasonalNudge: ()=>{ recordPromptDismissed('seasonalNudge'); setPendingSeasonalNudge(false); },
            troubleNudgeTick,
            showReviewCard,
            onReviewDismiss: () => {
              setReviewPromptState(prev => ({ ...prev, count: (prev.count||0)+1, lastShownAt: Date.now(), dismissedAt: Date.now() }));
              setReviewCardClosed(true);
            },
            onReviewCta: () => {
              setReviewPromptState(prev => ({ ...prev, count: (prev.count||0)+1, lastShownAt: Date.now(), mutedAt: Date.now() }));
              setReviewCardClosed(true);
              // Phase 32 D-01 — review_prompt_accepted fires on the CTA tap (user chose to leave a review)
              logEvent('review_prompt_accepted');
              setShowReviewForm(true);
            },
            simulateInspireMe: devModeActive && simulateInspireMe,
            simulateFeaturedSupplier: devModeActive && simulateFeaturedSupplier,
            commercialFeaturesEnabled,
            // Phase 40 Plan 02 Task 4 — SaveToCloudCard rotation slot for unauthed users
            // with ≥1 local plant. The dismissedTips id 'save-to-cloud' is gated by
            // HomePanel's existing rotation-slot dismiss machinery (14-day reappearance).
            showSaveToCloud: !auth.session && (wPlants.length >= 1),
            onSaveToCloudSignIn: () => setShowSignInModal(true),
            onSaveToCloudDismiss: () => {},
            // Phase 40 Plan 04 Task 3 — PlantCapBanner (D-18 / UI-SPEC #8).
            // Banner visibility gated inside HomePanel on tier === 'free' && plants.length >= 25.
            tier: auth.tier,
            onShowPremium: () => setShowPremium(true),
          }}/>}

        {/* Month — extracted to src/views/month/MonthView.jsx (Plan 37-07 continuation) */}
        {view==="month"&&<MonthView
          wPlants={wPlants} monthPlants={monthPlants} selectedMonth={selectedMonth}
          filterTypes={filterTypes} setFilterTypes={setFilterTypes}
          showDone={showDone} setShowDone={setShowDone}
          expandedPlantId={expandedPlantId} toggleExpand={toggleExpand}
          isDone={isDone} isDismissed={isDismissed}
          toggleDone={toggleDone} toggleDismiss={toggleDismiss}
          pushUndo={pushUndo}/>}

        {/* Session */}
        {view==="session"&&<GardenSession plants={applyAreaFilter(plants)} isDone={isDone} isDismissed={isDismissed} onToggleDone={toggleDone} onDismiss={toggleDismiss} selectedTaskType={sessionTaskType} onSelectTaskType={setSessionTaskType} completions={completions} pushUndo={pushUndo}/>}

        {/* Shed Inventory */}
        {view==="shed"&&<ShedInventory plants={plants} isDone={isDone} inventory={inventory} setInventory={setInventory} resolvedRegion={resolvedRegion} commercialFeaturesEnabled={commercialFeaturesEnabled}/>}

        {/* Colour Palette */}
        {view==="palette"&&<ColourPalette plants={applyAreaFilter(plants)}/>}

        {/* Plants — extracted to src/views/plants/PlantsView.jsx (Plan 37-07 continuation) */}
        {view==="plants"&&<PlantsView
          searchQuery={searchQuery} setSearchQuery={setSearchQuery}
          sortOrder={sortOrder} setSortOrder={setSortOrder} sortedPlants={sortedPlants}
          plants={plants} areas={areas} completions={completions}
          plantNotes={plantNotes} setPlantNotes={setPlantNotes}
          showAddSheet={showAddSheet} setShowAddSheet={setShowAddSheet}
          showAddForm={showAddForm} setShowAddForm={setShowAddForm}
          showImport={showImport} setShowImport={setShowImport}
          importMode={importMode} setImportMode={setImportMode}
          newPlant={newPlant} setNewPlant={setNewPlant} addPlant={addPlant}
          effectiveMaxPlants={effectiveMaxPlants} atPlantCap={atPlantCap}
          showCapNotice={showCapNotice} setShowCapNotice={setShowCapNotice}
          expandedPlantId={expandedPlantId} toggleExpand={toggleExpand}
          isDone={isDone} toggleDone={toggleDone}
          removePlant={removePlant} updatePlant={updatePlant}
          sickPlantBannerVisible={sickPlantBannerVisible}
          setSickPlantBannerVisible={setSickPlantBannerVisible}
          troubleInlineByPlant={troubleInlineByPlant}
          setTroubleInlineByPlant={setTroubleInlineByPlant}
          setTroubleNudgeTick={setTroubleNudgeTick}
          aiReady={aiReady} aiStatus={aiStatus} reprobeAi={reprobeAi}
          aiStatusSetters={aiStatusSetters}
          effectiveFeatures={effectiveFeatures} featureTier={featureTier}
          upgradePromptState={upgradePromptState} forceUpgradePreview={forceUpgradePreview}
          shouldShowUpgrade={shouldShowUpgrade}
          recordPromptShown={recordPromptShown} recordPromptDismissed={recordPromptDismissed}
          logUpgradePromptClick={logUpgradePromptClick}
          setShowPremium={setShowPremium} setView={setView}
          resolvedRegion={resolvedRegion} reportedImages={reportedImages}
          handleReportImage={handleReportImage}
          nextPlantId={nextPlantId} logFirstPlantIfApplicable={logFirstPlantIfApplicable}
          setPlants={setPlants} dismissPastTasks={dismissPastTasks}
          tier={auth.tier}/>}

        {/* Print view — SC-01 */}
        {view==="print"&&<PrintView month={selectedMonth} plants={applyAreaFilter(plants)} isDone={isDone} onBack={()=>setView("month")}/>}

        {/* Admin panel — email-gated, lazy-loaded */}
        {view==="admin"&&<AdminPanel onBack={()=>setView("year")}/>}

        {/* Ask Tend — upgrade prompt for free tier, full chat for premium */}
        {view==="ask"&&(effectiveFeatures.ask_tend
          ? <AskTend plants={plants} areas={areas} serverKeyReady={serverKeyReady} aiReady={aiReady} aiStatus={aiStatus} onAiFailure={reprobeAi} aiStatusSetters={aiStatusSetters} maxPlants={effectiveMaxPlants} features={effectiveFeatures} savedMessages={askTendMessages} onSaveMessages={setAskTendMessages} onAddPlant={(p)=>{if(plants.length>=effectiveMaxPlants)return;const {areaId,subArea,_needsAreaConfirm,_fallbackDefault,...rest}=p;const locations=p.locations&&p.locations.length?p.locations:[{areaId:areaId||areas[0]?.id,subArea:subArea||""}];/* Phase 34 / RES-01 / D-03 — honour the fallback flag from AskTend's AI-failure paths; otherwise keep the existing 'ask-tend' provenance. */const provenance=_fallbackDefault?'fallback-default':'ask-tend';const np={...rest,locations,id:nextPlantId(plants),addedVia:provenance};logFirstPlantIfApplicable('ask-tend');setPlants(prev=>[...prev,np]);dismissPastTasks(np);}} resolvedRegion={resolvedRegion} inventory={inventory} experience={onboardData.experience || ''} commercialFeaturesEnabled={commercialFeaturesEnabled}/>
          : <AskTendUpgrade onShowPremium={()=>setShowPremium(true)}/>
        )}

        </Suspense>
      </div>
      {/* Simulating Premium indicator — shown when dev mode simulate is active */}
      {devModeActive&&simulatePremium&&(
        <div style={{position:"fixed",bottom:60,left:"50%",transform:"translateX(-50%)",background:"#fff",color:DEV_BLUE,fontSize:'0.6875rem',fontWeight:700,padding:"4px 12px",borderRadius:8,zIndex:250,boxShadow:"0 2px 8px rgba(0,0,0,0.15)",border:`1px solid ${DEV_BLUE}`,pointerEvents:"none",whiteSpace:"nowrap"}}>
          Simulating Premium
        </div>
      )}
      {showPremium&&<WhyGoPremium onClose={()=>setShowPremium(false)}/>}
      {showSettings&&<SettingsPanel darkMode={darkMode} darkModePref={darkModePref} cycleDarkMode={cycleDarkMode} textSize={textSize} onTextSizeChange={setTextSize} devModeAllowed={devModeAllowed} devModeActive={devModeActive} setDevModeActive={setDevModeActive} featureTier={featureTier} simulatePremium={simulatePremium} onSimulatePremium={setSimulatePremium} simulateUpgradePrompts={simulateUpgradePrompts} onSimulateUpgradePrompts={setSimulateUpgradePrompts} simulateReviewPrompt={simulateReviewPrompt} onSimulateReviewPrompt={setSimulateReviewPrompt} simulateInspireMe={simulateInspireMe} onSimulateInspireMe={setSimulateInspireMe} simulateFeaturedSupplier={simulateFeaturedSupplier} onSimulateFeaturedSupplier={setSimulateFeaturedSupplier} onDevSimulateReset={resetPromptStateOnDevToggle} onShowPremium={()=>{setShowPremium(true);setShowSettings(false);}} plants={plants} regionPref={regionPref} resolvedRegion={resolvedRegion} onRegionChange={changeRegion} devActions={{clearPlants:()=>setPlants([]),resetCompletions:()=>setCompletions({}),resetDismissedTips:()=>localStorage.removeItem("tend:dismissedtips"),loadTestData:()=>{setPlants(INITIAL_PLANTS);setAreas(INITIAL_AREAS);localStorage.setItem('tend:dataversion',String(DATA_VERSION));},viewEvents:()=>{setShowSettings(false);setShowEventsViewer(true);},clearAnalytics:()=>{clearEvents();}}} onShowTour={()=>{setShowSettings(false);handleShowTour();}} onManageSpaces={()=>{setShowSettings(false);setShowAreaMgr(true);}} onDeleteAllData={()=>{
        const plantCount=plants.length;
        const areaCount=areas.length;
        const noteCount=Object.keys(plantNotes||{}).length;
        const taskCount=countAllTasks(plants);
        setConfirmModal({
          title:"Delete all data",
          message:`This will permanently delete ${plantCount} plant${plantCount!==1?'s':''}, ${areaCount} space${areaCount!==1?'s':''}, ${noteCount} note${noteCount!==1?'s':''}, and ${taskCount} task${taskCount!==1?'s':''}, and reset onboarding. This cannot be undone.`,
          confirmLabel:"Delete everything",
          typedConfirmString:"DELETE",
          onConfirm:()=>{ wipeAllTendData(); window.location.reload(); },
          onCancel:()=>setConfirmModal(null),
        });
      }} onClose={()=>setShowSettings(false)} authSession={auth.session} syncStatus={auth.syncStatus} deadLetterCount={auth.deadLetterCount} onSignIn={()=>{setShowSettings(false);setShowSignInModal(true);}} onSignOut={()=>{setShowSettings(false);auth.signOut();}} onRefreshDeadLetter={auth.refreshDeadLetterCount} onDeleteAccount={auth.deleteAccount} onLaunchAdmin={devModeActive?()=>{setShowSettings(false);setView('admin');}:null}/>}
      {showEventsViewer&&<EventsViewerModal onClose={()=>setShowEventsViewer(false)}/>}
      {showAbout&&<AboutPanel plants={plants} serverKeyReady={serverKeyReady} devModeAllowed={devModeAllowed} devModeActive={devModeActive} onActivateDevMode={()=>setDevModeActive(true)} tier={featureTier} onShowLegal={setLegalModal} dataVersion={DATA_VERSION} onClose={()=>setShowAbout(false)} commercialFeaturesEnabled={commercialFeaturesEnabled}/>}
      {legalModal&&<LegalModal type={legalModal} onClose={()=>setLegalModal(null)}/>}
      {showFeedback&&<FeedbackModal view={view} month={selectedMonth} onClose={()=>setShowFeedback(false)}/>}
      {showReviewForm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{width:"100%",maxWidth:420}}>
            <ReviewForm onClose={()=>setShowReviewForm(false)} onSubmitted={()=>{ /* mutedAt already set on CTA click */ }}/>
          </div>
        </div>
      )}
      {showShare&&<RecommendModal onClose={()=>setShowShare(false)}/>}
      {showAchievement&&<AchievementModal plants={plants} completions={completions} onClose={()=>setShowAchievement(false)}/>}
      {/* Phase 40 Plan 02 Task 4 — auth surfaces. SignInModal opens from
          SaveToCloudCard or Settings; FirstSignInUploadModal surfaces during
          the first-sign-in upload flow; SyncedToast confirms backup success. */}
      {showSignInModal && (
        <SignInModal
          onClose={() => setShowSignInModal(false)}
          onSuccessNavigate={() => setShowSignInModal(false)}
        />
      )}
      {auth.firstSignInState?.active && (
        <FirstSignInUploadModal
          plantCount={auth.firstSignInState.plantCount}
          areaCount={auth.firstSignInState.areaCount}
          status={auth.firstSignInState.status}
          errorMessage={auth.firstSignInState.errorMessage}
          onRetry={auth.retryFirstSignInUpload}
          onDismissError={auth.dismissFirstSignInError}
        />
      )}
      {auth.syncedToastVisible && (
        <SyncedToast onDismiss={() => { /* parent auto-clears via setTimeout */ }} />
      )}
      {undoEntry && (
        <UndoToast
          key={undoEntry.id}
          label={undoEntry.label}
          onUndo={undoEntry.onUndo}
          onDismiss={dismissUndo}
        />
      )}
      <AuthErrorBanner />

      {showAreaMgr&&<AreaManager areas={areas} setAreas={setAreas} plants={plants} setPlants={setPlants} setConfirmModal={setConfirmModal} onClose={()=>setShowAreaMgr(false)}/>}
      {confirmModal&&<ConfirmModal {...confirmModal} onCancel={()=>setConfirmModal(null)}/>}
      {isOnboardingOpen&&(
        <OnboardingWizard
          step={onboardingStep}
          setStep={setOnboardingStep}
          onDismiss={handleOnboardingDismiss}
          onComplete={handleOnboardingComplete}
          onboardData={onboardData}
          setOnboardData={setOnboardData}
          areas={areas}
          setAreas={setAreas}
          plants={plants}
          newPlant={newPlant}
          setNewPlant={setNewPlant}
          onAddPlant={addPlant}
          features={effectiveFeatures}
          aiReady={aiReady}
          aiStatus={aiStatus}
          onAiFailure={reprobeAi}
          setView={setView}
        />
      )}
      {storageWarning === 'storage-full' && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'var(--c-danger)', color: '#fff',
          padding: '12px 20px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          fontSize: '0.875rem', zIndex: 9999, fontFamily: "'Lora', serif"
        }}>
          <span>Storage is full — some changes may not have been saved. Try removing unused plants or clearing old data.</span>
          <button onClick={() => setStorageWarning(null)}
            style={{
              background: 'transparent', border: '1px solid #fff',
              color: '#fff', padding: '4px 12px', borderRadius: 4,
              cursor: 'pointer', fontSize: '0.8125rem', whiteSpace: 'nowrap', marginLeft: 12
            }}>
            Dismiss
          </button>
        </div>
      )}
      {/* Phase 34 IMG-01 / D-07 — report-image toast.
          Success copy is canonical and locked per CONTEXT.md `<specifics>` —
          do NOT paraphrase the "Busy Bees" line. */}
      {reportToast === 'success' && (
        <div role="status" aria-live="polite" style={{
          position: 'fixed', bottom: 16, left: 16, right: 16,
          background: 'var(--c-success, #5a7d5a)', color: '#fff',
          padding: '12px 20px', borderRadius: 12,
          fontSize: '0.875rem', zIndex: 9999, fontFamily: "'Lora', serif",
          textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          Our Busy Bees at Tend have been notified — we'll check it on an upcoming update. Thanks!
        </div>
      )}
      {reportToast === 'error' && (
        <div role="status" aria-live="polite" style={{
          position: 'fixed', bottom: 16, left: 16, right: 16,
          background: 'var(--c-danger)', color: '#fff',
          padding: '12px 20px', borderRadius: 12,
          fontSize: '0.875rem', zIndex: 9999, fontFamily: "'Lora', serif",
          textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          Couldn't send the report — please try again in a moment.
        </div>
      )}
    </div>
  );
}

// EventsViewerModal moved to src/modals/EventsViewerModal.jsx (Plan 37-06).

// SettingsPanel moved to src/modals/SettingsPanel.jsx (Plan 37-06; highest-risk modal — see RESEARCH §G).

// AboutPanel moved to src/modals/AboutPanel.jsx (Plan 37-06).

// AddPlantSheet moved to src/sheets/AddPlantSheet.jsx (Plan 37-05).

// FeedbackModal moved to src/modals/FeedbackModal.jsx (Plan 37-06).

// PrintView moved to src/views/print/PrintView.jsx (Plan 37-07 Task 1 Commit B).

// PlantNotesLog moved to src/views/plants/PlantNotesLog.jsx (Plan 37-07 Task 3 Commit C).

// AffiliateSheet moved to src/sheets/AffiliateSheet.jsx (Plan 37-05).

// findMatchingSuppliers moved to src/lib/suppliers.js (Plan 37-04).
// SupplierSheet moved to src/sheets/SupplierSheet.jsx (Plan 37-05).

// ShedInventory moved to src/views/supplies/ShedInventory.jsx (Plan 37-07
// Task 1 Commit E). Re-exported below so App.test.jsx + App.pests.test.jsx +
// App.manual.test.jsx imports keep working until Plan 37-09 sweeps tests.
export { ShedInventory };

// GardenSession + SessionBlock + SessionRow moved to src/views/activity/
// (Plan 37-07 Task 1 Commit F). DivisionGuide is reached from GardenSession on
// the "divide" task type — sibling-folder import inside the activity view.

// ─────────────────────────────────────────────────────────────────────────────
// COLOUR PALETTE
// ─────────────────────────────────────────────────────────────────────────────

// Fill forward from the nearest prior explicit entry, but only between defined
// entries — stop at the last one so winter/dormancy gaps are preserved.
// Exported so GardenSummaryCard (and any future colour-aware component) can
// share the same forward-fill semantics.
// resolveColour moved to src/lib/colours.js (Plan 37-07 Task 1 Commit A).
// Re-exported here so existing cross-folder consumers keep working until
// Plan 37-09 sweeps imports.
export { resolveColour };

// ─────────────────────────────────────────────────────────────────────────────
// SHARE FEATURE — MO-09
// ─────────────────────────────────────────────────────────────────────────────
// SHARE_PLATFORM_DIMS + share-card Canvas helpers moved to src/modals/share-canvas.js (Plan 37-06).
// RecommendModal moved to src/modals/RecommendModal.jsx (Plan 37-06).
// AchievementModal moved to src/modals/AchievementModal.jsx (Plan 37-06).
// getMonthColours + countCompletedTasks colocate inside AchievementModal.jsx — sole consumer.

// HOME PANEL — YV-01a/b/c/d/e
// ─────────────────────────────────────────────────────────────────────────────

// Shared dismiss button used by all content cards.
// Exported for cross-folder consumption by src/home-panel/* card components.
export function CardDismiss({onDismiss}) {
  return(
    <button onClick={onDismiss} style={{position:"absolute",top:10,right:10,background:"none",border:"none",cursor:"pointer",color:"var(--c-text-4)",padding:4,borderRadius:6,lineHeight:1,display:"flex"}} aria-label="Dismiss"><X size={15}/></button>
  );
}

// UpgradePrompt + UpgradePromptHost moved to src/home-panel/UpgradePrompt.jsx
// (Plan 37-04). Both ship from one file — the host is a one-line wrapper.

// GardenSummaryCard moved to src/home-panel/GardenSummaryCard.jsx (Plan 37-04).
// MOBILE_PILL_CAP colocated inside the new file (single consumer; was line 4849
// in the pre-split source). COLOUR_TYPE_ORDER (was line 4847) was unused after
// Phase 36 — confirmed by grep — so dropped from the codebase here rather than
// re-exporting dead code into HomePanel as Plan Task 2 originally hinted.

// SeasonalTipCard moved to src/home-panel/SeasonalTipCard.jsx (Plan 37-04).

// ReviewPromptCard moved to src/home-panel/ReviewPromptCard.jsx (Plan 37-04).

// ReviewForm moved to src/modals/ReviewForm.jsx (Plan 37-06).

// ── YV-01d Inventory alert card ──────────────────────────────────────────────
// InventoryAlertCard moved to src/home-panel/InventoryAlertCard.jsx (Plan 37-04).

// PlantGapCard moved to src/home-panel/PlantGapCard.jsx (Plan 37-04).

// ── InspireCard (ONB-04) — contextual plant suggestion on Home Panel ─────────
// TroubleProductCard moved to src/home-panel/TroubleProductCard.jsx (Plan 37-04).
// Cross-folder consumers (Plan 37-07): AskTend + PlantDirectoryCard will import
// from the new location once they themselves move; for now App.jsx imports the
// default and exposes it under the same name.

// InspireCard moved to src/home-panel/InspireCard.jsx (Plan 37-04).

// FeaturedSupplierCard moved to src/home-panel/FeaturedSupplierCard.jsx (Plan 37-04).

// ── YV-01a Home Panel shell ──────────────────────────────────────────────────
// HomePanel moved to src/home-panel/HomePanel.jsx (Plan 37-04 Task 2).
// SEASONAL_COPY is colocated inside the new file (HomePanel-internal).
// rankInspireCandidates moved with InspireCard. GAP_RULES moved with HomePanel.

// DivisionGuide + DivisionPlantCard moved to src/views/division/ (Plan 37-07
// Task 1 Commit C). Reached from GardenSession on the "divide" task type.

// ColourPalette moved to src/views/palette/ColourPalette.jsx (Plan 37-07
// Task 1 Commit D). Re-exported here so src/App.test.jsx keeps working until
// Plan 37-09 sweeps test imports.
export { ColourPalette };

// ─────────────────────────────────────────────────────────────────────────────
// BULK IMPORT PANEL  (DA-14)
// ─────────────────────────────────────────────────────────────────────────────
// BulkImportPanel moved to src/views/plants/BulkImportPanel.jsx
// (Plan 37-07 continuation — see file for AI batch import + offline static fallback).

// AskTendUpgrade moved to src/modals/AskTendUpgrade.jsx (Plan 37-06; Open Q 2).

// WhyGoPremium moved to src/modals/WhyGoPremium.jsx (Plan 37-06).

// ─────────────────────────────────────────────────────────────────────────────
// Phase 27 ENG-02 — buildShoppingStack (pure module-level helper)
//
// Merges symptom match (matchProductsInText) with shopping match
// (matchShoppingProductsInText), de-dupes, applies Phase 23 D-17 positional
// ranking (first-keyword-position in the user's message wins), filters products
// whose keywords never appear in the user's text (idx === Infinity), applies
// anti-repetition against the previous assistant message's _attachedProducts
// array, and suppresses inventory stock:'ok' products (D-06).
//
// Pure — no React state. Exported for unit testing in App.test.jsx.
// See RESEARCH.md §Code Examples — Shopping matcher caller diff.
// ─────────────────────────────────────────────────────────────────────────────
// buildShoppingStack moved to src/views/ask-tend/shopping.js (Plan 37-07
// Task 2 Commit A — split early because it's a pure helper). Re-exported
// below so App.test.jsx imports keep working.
export { buildShoppingStack };

// AskTend moved to src/views/ask-tend/AskTend.jsx
// (Plan 37-07 continuation — TroubleProductCard reached cross-folder).

// PlantCard moved to src/views/plants/PlantCard.jsx (Plan 37-07 Task 3 Commit A
// — canonical home; MonthView re-imports from here in Task 4).

// PlantHistory moved to src/views/plants/PlantHistory.jsx (Plan 37-07 Task 3 Commit B).

// PlantDirectoryCard moved to src/views/plants/PlantDirectoryCard.jsx
// (Plan 37-07 continuation — TroubleProductCard reached cross-folder).

// AreaManager moved to src/modals/AreaManager.jsx (Plan 37-06).

// ─────────────────────────────────────────────────────────────────────────────
// renderMarkdown moved to src/views/ask-tend/markdown.js (Plan 37-07 Task 2
// Commit A — split early because it's a pure helper). Re-exported below so
// App.test.jsx imports keep working.
export { renderMarkdown };

// lookupStaticSchedule, parseSchedule, callAI, parseColour
// moved to src/lib/ai.js (Plan 37-02).

// AddPlantForm moved to src/sheets/AddPlantForm.jsx (Plan 37-05).

// richDefaultTasks, defaultTasks moved to src/lib/ai.js (Plan 37-02).

// Phase 37 transitional re-export shim removed in Plan 37-09. Every helper and
// component now lives in its canonical home under src/{lib,home-panel,onboarding,
// sheets,modals,views}/ — call sites and tests import directly from there.
